import { Component, OnInit, Input, OnDestroy, Inject, ViewChild } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { StatementGeneratorFormComponent } from './statement-generator-form.component';
import { PicostNameDialogComponent } from './picost-name-dialog.component';
import { ModalSelectDocumentsComponent } from '../../modals/modal-select-documents/modal-select-documents.component';
import { ModalAddAttachmentComponent } from '../../modals/modal-add-attachment/modal-add-attachment.component';
import { StatementGeneratorService } from '../../services/statement-generator.service';
import { SurveyService } from '../../services/surveys.service';
import { UserService } from '../../services/user.service';
import { GradeService } from '../../services/grade.service';
import { AiService } from '../../services/ai.service';
import {
  StatementInputSource,
  GeneratedStatement,
  StatementGenerationRequest,
  StatementProcess,
  PICOInputData,
  ProjectDetailsInputData
} from '../../models/statement.models';
import {
  StatementGenerationRequest as NewStatementGenerationRequest,
  GenerationState,
  GenerationStatusResponse,
  CreateSurveyFromStatementsRequest,
  SectionWhispererStatusResponse,
  ProcessStatus
} from '../../models/statement-generator.models';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../services/general/toast.service';
import { BackendService } from '../../services/general/backend.service';
import * as XLSX from 'xlsx';

/** PubMed search strategy generated by Bedrock (ai/generate-pubmed-query). */
interface PubmedStrategy {
  base_query: string;
  /** Codes of PubmedController::EVIDENCE_FILTERS, in priority order */
  evidence_types: string[];
  /** Country name/adjective for the 'national' evidence type, e.g. ['Italy', 'Italian'] */
  country_terms?: string[];
  /** Landmark trial names/acronyms for the 'key_trials' evidence type, e.g. ['EPIC-HR'] */
  key_trials?: string[];
  /** Intervention block, required in the title for the broadest evidence types */
  intervention_block?: string;
  /** Outcome block, required for primary studies and meta-analyses */
  outcome_block?: string;
  date_from: number | null;
  date_to: number | null;
}

@Component({
    selector: 'app-statement-generator',
    templateUrl: './statement-generator-integrated.component.html',
    styleUrls: ['./statement-generator.component.scss'],
    standalone: false
})
export class StatementGeneratorComponent implements OnInit, OnDestroy {

  @ViewChild('formRef') formRef!: StatementGeneratorFormComponent;

  @Input() projectId: number;
  @Input() projectData: any;
  @Input() picoData: PICOInputData;

  selectedInputs: StatementInputSource[] = [];
  generatedStatements: GeneratedStatement[] = [];

  // Tab management
  selectedTabIndex = 0;  // 0 = PICO, 1 = Literature Upload, 2 = Structured Form, 3 = Generated Statements

  // PICO tab
  patients = '';
  intervention = '';
  comparison = '';
  outcomes = '';
  studies = '';
  timeframe = '';

  // patients: string = 'Pazienti anziani con diabete di tipo 2';
  // intervention: string = 'Una dieta a basso contenuto di carboidrati';
  // comparison: string = 'Rispetto alla dieta standard';
  // outcomes: string = 'Migliorare il controllo glicemico';

  // patients: string = "Pazienti adulti con ipertensione";
  // intervention: string = "Assunzione di farmaci anti-ipertensivi";
  // comparison: string = "Cambiamento dello stile di vita (dieta e attività fisica)";
  // outcomes: string = "Riduzione della pressione arteriosa";

  // patients: string = 'Lattanti (età < 12 mesi) con diagnosi di bronchiolite acuta moderata';
  // intervention: string = 'Nebulizzazione con soluzione salina ipertonica (al 3%)';
  // comparison: string = 'Nebulizzazione con soluzione salina normale (isotonica, allo 0.9%) o nessuna nebulizzazione (terapia di supporto standard)';
  // outcomes: string = 'Riduzione della durata del ricovero ospedaliero e miglioramento dei punteggi di gravità clinica(es.score respiratorio)';

  picoQuestionStatus: string | null = null;
  question = '';
  keywordsForChipList: string[] = [];
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  private _picoOriginal = { patients: '', intervention: '', comparison: '', outcomes: '', studies: '', timeframe: '' };

  get projectUpdateModelIsChanged(): boolean {
    return this.patients !== this._picoOriginal.patients ||
           this.intervention !== this._picoOriginal.intervention ||
           this.comparison !== this._picoOriginal.comparison ||
           this.outcomes !== this._picoOriginal.outcomes ||
           this.studies !== this._picoOriginal.studies ||
           this.timeframe !== this._picoOriginal.timeframe;
  }

  setUpdateProjectModel(): void {
    this.patients = this._picoOriginal.patients;
    this.intervention = this._picoOriginal.intervention;
    this.comparison = this._picoOriginal.comparison;
    this.outcomes = this._picoOriginal.outcomes;
    this.studies = this._picoOriginal.studies;
    this.timeframe = this._picoOriginal.timeframe;
  }

  generatePICOQuestion(): void {
    this.picoQuestionStatus = 'pending';
    // A new question/keywords set makes the previous search strategy stale
    this.setPubmedStrategy(null);
    const data = {
      organization_id: this.projectData?.organization_id,
      picoStructure: {
        patients: this.patients,
        intervention: this.intervention,
        comparison: this.comparison,
        outcomes: this.outcomes,
        studies: this.studies,
        timeframe: this.timeframe
      }
    };
    this.aiService.generatePICOResponse(
      data,
      (result) => {
        if (result) {
          let jsonString = result.response
            .replace(/```json/gi, '').replace(/```/g, '').replace(/`/g, '').trim();
          const jsonMatch = jsonString.match(/\{[\s\S]*\}/);
          if (jsonMatch) jsonString = jsonMatch[0];
          try {
            const parsed = JSON.parse(jsonString);
            this.question = parsed.question || '';
            if (Array.isArray(parsed.keywords)) {
              this.keywordsForChipList = parsed.keywords;
            } else if (parsed.keywords) {
              this.keywordsForChipList = parsed.keywords.toString().split(',').map((k: string) => k.trim()).filter(Boolean);
            }
          } catch {
            this.question = result.response;
            this.keywordsForChipList = [];
          }
        }
        this.picoQuestionStatus = 'ready';
      },
      () => { this.picoQuestionStatus = null; }
    );
  }

  addKeyword(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.keywordsForChipList.push(value);
      // Keywords feed the optimized query: it has to be generated again
      this.setPubmedStrategy(null);
    }
    event.chipInput!.clear();
  }

  removeKeyword(keyword: string): void {
    const index = this.keywordsForChipList.indexOf(keyword);
    if (index >= 0) {
      this.keywordsForChipList.splice(index, 1);
      this.setPubmedStrategy(null);
    }
  }

  // Saving the current PICOST (question + keywords + framework) to grade_revisions
  savingPicost = false;

  // Saved PICOSTs (past generations) for this project, read from grade_revisions
  savedPicosts: any[] = [];
  loadingSavedPicosts = false;

  /**
   * Load the PICOSTs already saved for this project (grade_revisions rows tagged
   * as PICOST presets), shown in the table in the P.I.C.O.S.T. tab.
   */
  loadSavedPicosts(): void {
    if (!this.projectId) return;
    this.loadingSavedPicosts = true;
    this.gradeService.getPicostPresets(this.projectId).subscribe({
      next: (presets) => {
        this.savedPicosts = presets || [];
        this.loadingSavedPicosts = false;
      },
      error: () => {
        this.savedPicosts = [];
        this.loadingSavedPicosts = false;
      }
    });
  }

  /**
   * Load a saved PICOST back into the P.I.C.O.S.T. form fields (and its keywords
   * / question, when present).
   */
  loadSavedPicost(picost: any): void {
    const p = picost?.search_parameters?.picost || {};
    this.patients = p.population || '';
    this.intervention = p.intervention || '';
    this.comparison = p.comparator || '';
    this.outcomes = p.outcomes || '';
    this.studies = p.studies || p.setting || '';
    this.timeframe = p.timeframe || '';

    // Always reset, otherwise the previously loaded PICOST's keywords/question
    // would stay on screen (and be used for the PubMed search).
    this.keywordsForChipList = picost?.keywords
      ? picost.keywords.split(',').map((k: string) => k.trim()).filter(Boolean)
      : [];
    this.question = picost?.description || picost?.search_query || '';
    // The saved PubMed strategy is not restored: before searching, the user must
    // generate the optimized query again from the loaded PICOST
    this.setPubmedStrategy(null);
    // After loading a saved PICOST the framework is already complete, so treat it
    // as a generated question: this reveals the keywords chip list and the
    // "search on PubMed" button without having to regenerate the PICO question.
    this.picoQuestionStatus = 'ready';
    this.showMessage('PICOST caricato', 'success');
  }

  /** Delete a saved PICOST from grade_revisions. */
  deleteSavedPicost(picost: any, event?: Event): void {
    event?.stopPropagation();
    if (!picost?.id) return;
    if (!confirm(`Eliminare il PICOST "${picost.title}"?`)) return;
    this.gradeService.deletePicostPreset(picost.id).subscribe({
      next: () => {
        this.savedPicosts = this.savedPicosts.filter(x => x.id !== picost.id);
        this.showMessage('PICOST eliminato', 'success');
      },
      error: () => this.showMessage('Errore durante l\'eliminazione del PICOST', 'error')
    });
  }

  /**
   * Save the current PICOST — the generated question, its keywords and the
   * PICOST framework fields — into the grade_revisions table, so this
   * generation can be reviewed/reused later. Optional: triggered only when the
   * user clicks the Save button in the P.I.C.O.S.T. tab.
   */
  savePicost(): void {
    const suggested = `PICOST – ${new Date().toLocaleDateString()}`;

    this.dialog.open(PicostNameDialogComponent, {
      width: '480px',
      maxWidth: '90vw',
      data: { suggestedName: suggested }
    }).afterClosed().subscribe((title: string | undefined) => {
      const name = (title || '').trim();
      if (!name) return; // cancelled or empty
      this.persistPicost(name);
    });
  }

  /**
   * Persist the current PICOST to the grade_revisions table (via the
   * grade/revisions backend endpoint): the generated question, its keywords and
   * the PICOST framework fields.
   */
  private persistPicost(title: string): void {
    const keywords = this.keywordsForChipList.join(', ');
    const picoQuestion = this.question ||
      `P: ${this.patients} I: ${this.intervention} C: ${this.comparison} O: ${this.outcomes}`;

    this.savingPicost = true;
    this.gradeService
      .savePicostPreset({
        title,
        organization_project_id: this.projectId,
        organization_id: this.projectData?.organization_id ?? undefined,
        picost: {
          population: this.patients || '',
          intervention: this.intervention || '',
          comparator: this.comparison || '',
          outcomes: this.outcomes || '',
          timeframe: this.timeframe || '',
          studies: this.studies || ''
        },
        keywords: keywords || undefined,
        search_query: picoQuestion,
        description: this.question || undefined,
        pubmed_query: this.pubmedQuery.trim() || undefined,
        pubmed_strategy: this.pubmedStrategy || undefined,
      })
      .subscribe({
        next: () => {
          this.savingPicost = false;
          this.showMessage('PICOST salvato', 'success');
          this.loadSavedPicosts();
        },
        error: () => {
          this.savingPicost = false;
          this.showMessage('Errore nel salvataggio del PICOST', 'error');
        },
      });
  }


  previousSurveysAvailable: any[] = [];
  existingPicos: any[] = [];
  existingLiterature: any[] = [];

  // Scientific papers upload
  uploadedPapers: any[] = [];
  isProcessingPapers = false;
  papersFilterText = '';

  isGenerating = false;
  currentProcess: StatementProcess | null = null;
  generationProgress = 0;
  generationMessage = '';

  // Needs input state
  needsInput = false;
  needsInputMessage = '';
  needsInputSuggestions: string[] = [];

  // Auto-save configuration
  // Backend endpoints are now implemented, auto-save is ENABLED
  // - POST /api/projects/{projectId}/statements ✅
  // - GET /api/projects/{projectId}/statements ✅
  enableAutoSave = true;

  // Polling subscription
  private pollingSubscription: Subscription | null = null;

  // Status polling interval
  private statusPollingInterval: any = null;

  // Display columns for statements table
  displayedColumns: string[] = ['number', 'statement', 'section', 'comment', 'loe', 'gor', 'references', 'actions'];

  // New Statement Generator (AWS Lambda-based)
  newGenerationState: GenerationState = {
    processId: null,
    processName: null,
    status: 'idle',
    progress: 0,
    message: '',
    error: null,
    statements: [],
    sections: [],
    surveyMetadata: null,
    selectedStatementIds: [],
    targetStatements: 4,
    generatedCount: 0,
    appliedToggles: null
  };

  showProgressMonitor = false;
  showStatementReview = false;
  lastCreatedSurveyId: number | null = null;
  private newPollingSubscription: Subscription | null = null;

  // ── Section Whisperer step (form → sections → review → generator) ──────────
  // Shown between the input form and statement generation: the whisperer runs,
  // then the user reviews/edits the section list before generating statements.
  showSectionReview = false;
  showSectionProgress = false;
  sectionState: {
    processId: string | null;
    status: ProcessStatus;
    progress: number;
    message: string;
    error: string | null;
    sections: string[];
  } = {
    processId: null,
    status: 'idle',
    progress: 0,
    message: '',
    error: null,
    sections: []
  };
  // Editable section titles the user reviews. Bound to the review UI.
  editableSections: string[] = [];
  newSectionTitle = '';
  // The generator-shaped payload captured at form submit, reused when the user
  // confirms the reviewed sections and moves on to statement generation.
  private pendingFormData: any = null;
  private sectionPollingSubscription: Subscription | null = null;

  // In-progress generations for this project, re-attached when the accordion is
  // opened / the page reloads. One progress card is rendered per entry.
  resumedGenerations: (GenerationStatusResponse & { active: boolean })[] = [];
  private resumedPollSubs = new Map<string, Subscription>();

  // Full generation history for this project (the "Past Generations" tab).
  pastGenerations: any[] = [];
  loadingPastGenerations = false;

  biblioSearchStatus: string | null = null;
  // Total papers, split evenly across the selected evidence types by the stratified search
  pubmedSearchLimit: number = 30;

  // Bedrock-generated PubMed strategy: read-only base clinical query + evidence types
  // (searched once each, toggleable). Null = search with the keywords.
  pubmedStrategy: PubmedStrategy | null = null;
  selectedEvidenceTypes = new Set<string>();
  // Per-evidence-type counts of the last stratified search
  pubmedStrata: { type: string; count_total: number; returned: number }[] = [];
  pubmedQueryStatus: string | null = null;

  get pubmedQuery(): string {
    return this.pubmedStrategy?.base_query || '';
  }

  private setPubmedStrategy(strategy: PubmedStrategy | null): void {
    this.pubmedStrategy = strategy;
    this.selectedEvidenceTypes = new Set(strategy?.evidence_types || []);
    this.pubmedStrata = [];
  }

  // Idempotent: mat-chip-option also emits selectionChange when [selected] is set programmatically
  setEvidenceTypeSelected(type: string, selected: boolean): void {
    if (selected) {
      this.selectedEvidenceTypes.add(type);
    } else {
      this.selectedEvidenceTypes.delete(type);
    }
  }

  evidenceStratum(type: string) {
    return this.pubmedStrata.find(s => s.type === type);
  }

  constructor(
    private dialog: MatDialog,
    private statementService: StatementGeneratorService,
    private surveysService: SurveyService,
    public userService: UserService,
    private gradeService: GradeService,
    private aiService: AiService,
    private backend: BackendService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private toast: ToastService
  ) { }

  /**
   * Ask Bedrock for a PubMed search strategy built from the current PICOST,
   * question and keywords: an English base clinical query (MeSH + [tiab]) plus
   * the evidence types named by the Studies field and the date range.
   */
  generatePubmedQuery(): void {
    this.pubmedQueryStatus = 'pending';
    this.aiService.generatePubmedQuery(
      {
        organization_id: this.projectData?.organization_id,
        picoStructure: {
          patients: this.patients,
          intervention: this.intervention,
          comparison: this.comparison,
          outcomes: this.outcomes,
          studies: this.studies,
          timeframe: this.timeframe
        },
        question: this.question,
        keywords: this.keywordsForChipList
      },
      (result) => {
        const baseQuery = result?.base_query || result?.query || '';
        this.setPubmedStrategy(baseQuery ? {
          base_query: baseQuery,
          evidence_types: result?.evidence_types?.length ? result.evidence_types : ['other'],
          country_terms: result?.country_terms || [],
          key_trials: result?.key_trials || [],
          intervention_block: result?.intervention_block || '',
          outcome_block: result?.outcome_block || '',
          date_from: result?.date_from ?? null,
          date_to: result?.date_to ?? null
        } : null);
        this.pubmedQueryStatus = null;
      },
      () => {
        this.pubmedQueryStatus = null;
        this.showMessage('modals.select-inputs.pubmed-query-error', 'error');
      }
    );
  }

  /**
   * Search PubMed with the Bedrock strategy when present: one search per selected
   * evidence type, merged by the backend (search-stratified). Otherwise search
   * with the keywords. If the strategy fails or finds nothing, fall back to the
   * keyword search.
   */
  searchTermInPublmedEnhanced(saveToProject: boolean = true): void {
    this.biblioSearchStatus = 'pending';
    if (this.pubmedStrategy?.base_query && this.selectedEvidenceTypes.size) {
      this.runStratifiedSearch(this.pubmedStrategy, saveToProject);
    } else {
      this.runKeywordSearch(saveToProject);
    }
  }

  private runStratifiedSearch(strategy: PubmedStrategy, saveToProject: boolean): void {
    const used: PubmedStrategy = {
      ...strategy,
      evidence_types: strategy.evidence_types.filter(t => this.selectedEvidenceTypes.has(t))
    };

    this.backend.post('tools/pubmed/search-stratified', {
      base_query: used.base_query,
      evidence_types: used.evidence_types,
      country_terms: used.country_terms || [],
      key_trials: used.key_trials || [],
      intervention_block: used.intervention_block || '',
      outcome_block: used.outcome_block || '',
      date_from: used.date_from,
      date_to: used.date_to,
      maxResultsPerPage: this.pubmedSearchLimit
    }).subscribe({
      next: (result: any) => {
        this.pubmedStrata = result?.strata || [];
        const articles = result?.articles || [];
        if (!articles.length) { this.fallbackToKeywordSearch(saveToProject); return; }
        this.handlePubmedArticles(articles, used, saveToProject);
      },
      error: () => this.fallbackToKeywordSearch(saveToProject)
    });
  }

  private runKeywordSearch(saveToProject: boolean): void {
    const payload = { term: this.keywordsForChipList.join(', '), page: 1, maxResultsPerPage: this.pubmedSearchLimit };
    this.backend.post('tools/pubmed/search-extended', payload).subscribe({
      next: (result: any) => {
        const articles = result?.articles || [];
        if (!articles.length) { this.biblioSearchStatus = null; return; }
        this.handlePubmedArticles(articles, null, saveToProject);
      },
      error: () => { this.biblioSearchStatus = null; }
    });
  }

  private fallbackToKeywordSearch(saveToProject: boolean): void {
    this.showMessage('modals.select-inputs.pubmed-query-fallback', 'warning');
    this.runKeywordSearch(saveToProject);
  }

  private handlePubmedArticles(articles: any[], strategy: PubmedStrategy | null, saveToProject: boolean): void {
    const query = this.keywordsForChipList.join(', ');
    const picoQuestion = this.question ||
      `P: ${this.patients} I: ${this.intervention} C: ${this.comparison} O: ${this.outcomes}`;

    const newArticles: any[] = [];
    articles.forEach((a: any) => {
      const pmid = a.PMID;
      if (!this.uploadedPapers.some(p => p.PMID === pmid)) {
        const paper = {
          title: a.ArticleTitle || a.title,
          authors: a.Authors || a.authors,
          year: a.PubYear || a.year,
          PMID: pmid,
          link: a.link || `https://pubmed.ncbi.nlm.nih.gov/${pmid}`,
          type: 'PUBMED',
          evidence_type: a.evidence_type || null,
          status: 'waiting',
          llm_analysis: null
        };
        this.uploadedPapers.push(paper);
        newArticles.push(paper);
      }
    });

    const organizationId = this.projectData?.organization_id;
    newArticles.forEach(p => {
      this.aiService.savePubmedDocument(
        organizationId,
        this.projectId,
        { PMID: p.PMID, ArticleTitle: p.title, link: p.link, Authors: p.authors, PubYear: p.year },
        (response) => {
          const saved = this.uploadedPapers.find(up => up.PMID === p.PMID);
          if (saved) {
            saved.id           = response.id || response.file_id;
            saved.status       = response.status       || 'waiting';
            saved.authors      = response.authors      || saved.authors;
            saved.year         = response.year         || saved.year;
            saved.study_type   = response.study_type   || saved.study_type;
            saved.main_outcome = response.main_outcome || saved.main_outcome;
            saved.llm_analysis = response.llm_analysis || saved.llm_analysis;
          }
        },
        (error) => console.error('Failed to save pubmed document', p.PMID, error)
      );
    });

    if (saveToProject) {
      const data = {
        organization_id: this.projectData?.organization_id,
        organization_project_id: this.projectId,
        statement: articles.map((a: any) => ({
          title: a.ArticleTitle, authors: a.Authors, PMID: a.PMID,
          pubyear: a.PubYear, link: a.link
        })),
        question: this.question,
        keywords: query,
        pico_question: picoQuestion,
        search_query: picoQuestion,
        search_parameters: {
          patients: this.patients, intervention: this.intervention,
          comparison: this.comparison, outcomes: this.outcomes,
          studies: this.studies, timeframe: this.timeframe, keywords: query,
          pubmed_query: strategy?.base_query || undefined,
          pubmed_strategy: strategy || undefined
        }
      };
      this.aiService.generateLiteratireReviewTableWithPubmedArticlesEnhanced(
        data, true,
        () => { this.biblioSearchStatus = null; },
        () => { this.biblioSearchStatus = null; }
      );
    } else {
      this.biblioSearchStatus = null;
    }

    this.selectedTabIndex = 1;
  }

  ngOnInit(): void {
    // Auto-add project details and PICO if available
    this.autoAddProjectContext();

    // Load previous surveys for the structured form
    this.loadPreviousSurveys();

    // Load existing PICOs for the structured form
    this.loadExistingPicos();

    // Load existing literature for the structured form (PubMed only, no GRADE)
    this.loadExistingLiterature();

    // Load project attachments and their processing status
    this.loadProjectAttachments();

    // Load existing draft statements for the project
    this.loadProjectDraftStatements();


    // Load PICOSTs already saved for this project (shown in the P.I.C.O.S.T. tab table)
    this.loadSavedPicosts();
  }

  /**
   * Called when the "P.I.C.O - Statement Generator" accordion is opened. Shows
   * a progress card for every in-progress generation of this project (and any
   * recently-failed/timed-out one) — even if the user reloaded the page or
   * navigated away while they were running.
   */
  onAccordionOpened(): void {
    this.resumeInProgressGenerations();
    this.loadPastGenerations();
  }

  /**
   * Load the project's full generation history for the "Past Generations" tab.
   */
  loadPastGenerations(): void {
    if (!this.projectId) return;
    this.loadingPastGenerations = true;
    this.statementService.getProjectProcesses(
      this.projectId,
      (res) => {
        this.pastGenerations = res?.processes || [];
        this.loadingPastGenerations = false;
      },
      (error) => {
        console.error('Error loading past generations:', error);
        this.loadingPastGenerations = false;
      }
    );
  }

  /** Past-generations row: load that generation's statements into the review. */
  viewPastStatements(p: any): void {
    if (!p?.id) return;
    this.newGenerationState.processId = String(p.id);
    this.loadGeneratedStatementsNew();
  }

  /**
   * Show ALL statements generated across ALL generations of this project in the
   * "Statement Generati" tab.
   */
  viewAllStatements(): void {
    if (!this.projectId) return;
    this.statementService.getProjectDraftStatements(
      this.projectId,
      (response) => {
        this.newGenerationState.statements = response.statements || [];
        this.showStatementReview = true;
        this.showProgressMonitor = false;
        this.selectedTabIndex = 3; // "Statement Generati" tab
      },
      (error) => {
        console.error('Error loading all statements:', error);
        this.showMessage('statement-generator.loading-failed', 'error');
      }
    );
  }

  /** Past-generations row: load that generation's input payload into the form. */
  loadPastInputData(p: any): void {
    if (!p?.id) return;
    this.loadInputDataFromProcess(String(p.id));
  }

  /** Past-generations card "close": hide it from the list for this session. */
  dismissPastGeneration(p: any): void {
    this.pastGenerations = this.pastGenerations.filter(x => String(x.id) !== String(p?.id));
  }

  /**
   * Fetch the in-progress generations for this project and render a live
   * progress card per ACTIVE (waiting/processing) row in the input form.
   *
   * Terminal generations (failed/completed) are intentionally NOT shown here —
   * the user doesn't want a failed/completed dialog box over the input form;
   * those are visible in the "Past Generations" tab instead.
   */
  private resumeInProgressGenerations(): void {
    if (!this.projectId) return;

    this.statementService.getRecentGenerations(
      this.projectId,
      (result) => {
        const processes = result?.processes || [];
        if (!processes.length) return;

        let shownAny = false;
        for (const p of processes) {
          if (!p || !p.id) continue;
          // Only show genuinely in-progress generations in the input form.
          if (!p.active) continue;

          const id = String(p.id);

          // Skip the process already shown by the form-driven flow, to avoid a
          // duplicate card.
          if (this.showProgressMonitor && this.newGenerationState.processId === id) continue;

          shownAny = true;

          // Upsert the card
          const idx = this.resumedGenerations.findIndex(g => String(g.id) === id);
          if (idx >= 0) {
            this.resumedGenerations[idx] = p;
          } else {
            this.resumedGenerations.push(p);
          }

          // Resume polling (guard against duplicates)
          if (!this.resumedPollSubs.has(id)) {
            this.startResumedPolling(id);
          }
        }

        // The cards live in the "Compilazione Guidata" tab (index 2)
        if (shownAny) {
          this.selectedTabIndex = 2;
        }
      },
      (error) => {
        // Non-fatal: if the check fails we just don't auto-resume
        console.error('Error checking for in-progress generations:', error);
      }
    );
  }

  /**
   * Poll a single resumed generation and update its card in place while it is
   * running. As soon as it reaches a terminal state (completed/failed) the card
   * is removed from the input form — terminal generations are not shown here,
   * only in the "Past Generations" tab — and that tab is refreshed.
   */
  private startResumedPolling(processId: string): void {
    const sub = this.statementService
      .pollGenerationStatus(processId, 2000, 15000, 1.5, 15 * 60 * 1000)
      .subscribe({
        next: (status: GenerationStatusResponse) => {
          const idx = this.resumedGenerations.findIndex(g => String(g.id) === processId);
          if (idx >= 0) {
            this.resumedGenerations[idx] = {
              ...this.resumedGenerations[idx],
              ...status,
              active: status.status === 'waiting' || status.status === 'processing'
            };
          }
        },
        complete: () => {
          this.resumedPollSubs.delete(processId);
          const g = this.resumedGenerations.find(x => String(x.id) === processId);
          // Completed → load the freshly generated statements into the review.
          if (g && g.status === 'completed') {
            this.loadProjectDraftStatements();
          }
          // Terminal (completed OR failed): drop the input-form card and let the
          // result show in the Past Generations tab.
          this.removeResumedGeneration(processId);
          this.loadPastGenerations();
        },
        error: () => {
          // Don't show a failed box over the input form; drop the card and let
          // the Past Generations tab reflect the real state.
          this.removeResumedGeneration(processId);
          this.loadPastGenerations();
        }
      });
    this.resumedPollSubs.set(processId, sub);
  }

  /** Stop polling a resumed generation and remove its card. */
  private removeResumedGeneration(processId: string): void {
    const sub = this.resumedPollSubs.get(processId);
    if (sub) {
      sub.unsubscribe();
      this.resumedPollSubs.delete(processId);
    }
    this.resumedGenerations = this.resumedGenerations.filter(g => String(g.id) !== processId);
  }

  /** Progress-card "close" — dismiss a resumed generation card. */
  handleCloseResumed(g: GenerationStatusResponse): void {
    this.removeResumedGeneration(String(g.id));
  }

  /** Progress-card "view statements" — load that process's statements. */
  handleViewResumedStatements(g: GenerationStatusResponse): void {
    this.newGenerationState.processId = String(g.id);
    this.loadGeneratedStatementsNew();
    this.removeResumedGeneration(String(g.id));
  }

  /**
   * Progress-card "retry" for a resumed generation. We don't have the original
   * form payload here, so we just dismiss the card and reset the form so the
   * user can submit a new generation.
   */
  handleRetryResumed(g: GenerationStatusResponse): void {
    this.removeResumedGeneration(String(g.id));
    this.formRef?.resetSubmissionState();
  }

  /** Progress-card "load input data" for a resumed generation card. */
  handleLoadInputDataResumed(g: GenerationStatusResponse): void {
    this.loadInputDataFromProcess(String(g.id));
  }

  /** Progress-card "load input data" for the form-driven generation monitor. */
  handleLoadInputData(): void {
    if (this.newGenerationState.processId) {
      this.loadInputDataFromProcess(this.newGenerationState.processId);
    }
  }

  /**
   * Fetch the payload that was sent for a generation and load it into the input
   * form (Compilazione Guidata tab) so the user can review / re-run it.
   */
  private loadInputDataFromProcess(processId: string): void {
    this.statementService.getGenerationRequestData(
      processId,
      (res) => {
        if (!res || !res.request_data) {
          this.showMessage('statement-generator.input-data-empty', 'warning');
          return;
        }
        this.formRef?.populateFromBackendPayload(res.request_data);
        this.selectedTabIndex = 2; // Compilazione Guidata tab hosts the form
        this.showMessage('statement-generator.input-data-loaded', 'success');
      },
      (error) => {
        console.error('Error loading generation request data:', error);
        this.showMessage('statement-generator.input-data-load-failed', 'error');
      }
    );
  }

  ngOnDestroy(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
    if (this.newPollingSubscription) {
      this.newPollingSubscription.unsubscribe();
    }
    if (this.sectionPollingSubscription) {
      this.sectionPollingSubscription.unsubscribe();
    }
    // Stop polling any resumed in-progress generations
    this.resumedPollSubs.forEach(sub => sub.unsubscribe());
    this.resumedPollSubs.clear();
    // Clear status polling interval
    this.stopStatusPolling();
  }

  /**
   * Stop status polling and clear interval
   */
  private stopStatusPolling(): void {
    if (this.statusPollingInterval) {
      clearInterval(this.statusPollingInterval);
      this.statusPollingInterval = null;
    }
  }

  /**
   * Get all available literature including existing literature and completed papers from the upload tab
   */
  get availableLiterature(): any[] {
    // Start with existing literature from GRADE revisions and PubMed
    const allLiterature = [...this.existingLiterature];

    // Add ALL papers from the Letteratura scientifica tab (FILE and PUBMED, any status)
    this.uploadedPapers.forEach(paper => {
      // Avoid duplicates (paper might already be in existingLiterature)
      const alreadyPresent = allLiterature.some(lit =>
        (lit.id && lit.id === paper.id) ||
        (lit.pmid && paper.PMID && lit.pmid === paper.PMID)
      );
      if (alreadyPresent) return;

      const literatureItem = {
        id: paper.id,
        title: paper.title || paper.ArticleTitle,
        type: paper.type,
        authors: paper.authors || '',
        year: paper.year || '',
        pmid: paper.PMID || paper.pmid || '',
        doi: paper.Doid || paper.DOI || paper.doi || '',
        nct: paper.nct || '',
        abstract: paper.abstract || '',
        key_findings: paper.llm_analysis || '',
        llm_analysis: paper.llm_analysis || '',
        link: paper.link || '',
        status: paper.status,
        source: 'upload_tab'
      };
      allLiterature.push(literatureItem);
    });

    return allLiterature;
  }

  /**
   * Check if all uploaded documents are completed (AI analysis already done)
   */
  get allDocumentsCompleted(): boolean {
    if (this.uploadedPapers.length === 0) {
      return false;
    }
    return this.uploadedPapers.every(paper => paper.status === 'completed');
  }

  /**
   * Disable the Input Form tab when there are papers whose AI analysis is not yet completed
   */
  get inputFormTabDisabled(): boolean {
    return this.uploadedPapers.length > 0 && !this.allDocumentsCompleted;
  }

  /**
   * Automatically add project details and PICO data as input sources
   */
  private autoAddProjectContext(): void {

    if (!this.projectData) {
      console.warn('No projectData available');
      return;
    }

    // ALWAYS add Project Details if projectData exists (removed strict condition)
    const projectDetails: ProjectDetailsInputData = {
      title: this.projectData.title || '',
      description: this.projectData.description || '',
      obiettivi: this.projectData.obiettivi || '',
      contesto_clinico_scientifico: this.projectData.contesto_clinico_scientifico || '',
      faculty: this.projectData.faculty || '',
      board: this.projectData.board || ''
    };

    this.selectedInputs.push({
      id: 'project-details',
      type: 'project_details',
      name: `Project: ${this.projectData.title || 'Untitled'}`,
      data: projectDetails,
      metadata: {
        uploadDate: new Date()
      }
    });

    // Add PICO if available
    if (this.picoData && (this.picoData.population || this.picoData.intervention)) {
      this.patients = this.picoData.population || '';
      this.intervention = this.picoData.intervention || '';
      this.comparison = this.picoData.comparison || '';
      this.outcomes = this.picoData.outcome || '';

      // Preserve any existing value if picoData doesn't carry these (so valued
      // studies/timeframe still show in the form).
      // this.studies = this.picoData.studies || this.studies || '';
      // this.timeframe = this.picoData.timeframe || this.timeframe || '';

      // this._picoOriginal = { patients: this.patients, intervention: this.intervention, comparison: this.comparison, outcomes: this.outcomes, studies: this.studies, timeframe: this.timeframe };

      this._picoOriginal = {
        patients: this.patients,
        intervention: this.intervention,
        comparison: this.comparison,
        outcomes: this.outcomes,
        studies: this.studies,
        timeframe: this.timeframe,
      };
      this.selectedInputs.push({
        id: 'pico-data',
        type: 'pico',
        name: 'PICO Question',
        data: this.picoData,
        metadata: {
          uploadDate: new Date(),
        },
      });
    } else {
      // console.log('No PICO data to add');
    }
  }

  /**
   * Open document/input selector modal
   */
  openInputSelector(): void {
    const dialogRef = this.dialog.open(ModalSelectDocumentsComponent, {
      width: '900px',
      maxHeight: '90vh',
      data: {
        projectId: this.projectId,
        projectData: this.projectData,
        selectedInputs: this.selectedInputs.filter(
          input => input.type !== 'project_details' && input.type !== 'pico'
        )
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.selectedInputs) {
        // Keep project_details and pico, replace others
        const contextInputs = this.selectedInputs.filter(
          input => input.type === 'project_details' || input.type === 'pico'
        );
        this.selectedInputs = [...contextInputs, ...result.selectedInputs];
      }
    });
  }

  /**
   * Build minimal context with only project and PICO data
   * Used to avoid backend parsing issues while still sending essential info
   */
  private buildMinimalContext(): any {

    const context: any = {};

    // Extract project data
    const projectInput = this.selectedInputs.find(i => i.type === 'project_details');

    if (projectInput && projectInput.data) {
      context.project = {
        title: this.sanitizeString(projectInput.data.title),
        obiettivi: this.sanitizeString(projectInput.data.obiettivi),
        contesto_clinico_scientifico: this.sanitizeString(projectInput.data.contesto_clinico_scientifico),
        faculty: this.sanitizeString(projectInput.data.faculty)
      };
    } else {
      console.warn('No project_details found in selectedInputs!');
    }

    // Extract PICO data
    const picoInput = this.selectedInputs.find(i => i.type === 'pico');

    if (picoInput && picoInput.data) {
      context.pico = {
        population: this.sanitizeString(picoInput.data.population),
        intervention: this.sanitizeString(picoInput.data.intervention),
        comparison: this.sanitizeString(picoInput.data.comparison),
        outcome: this.sanitizeString(picoInput.data.outcome)
      };
    }
    // else {
    //   console.log('No pico input found in selectedInputs');
    // }

    return context;
  }

  /**
   * Sanitize string to prevent prompt injection and parsing errors
   */
  private sanitizeString(str: string | null | undefined): string {
    if (!str) return '';

    // Convert to string and trim
    let sanitized = String(str).trim();

    // Remove or escape special characters that could break JSON/prompt parsing
    sanitized = sanitized
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '') // Remove control characters
      .replace(/\\/g, '\\\\') // Escape backslashes
      .replace(/"/g, '\\"') // Escape quotes
      .replace(/\n{3,}/g, '\n\n') // Reduce multiple newlines
      .replace(/\s{3,}/g, '  '); // Reduce multiple spaces

    // Limit length to prevent token overflow (max 2000 chars per field)
    if (sanitized.length > 2000) {
      sanitized = sanitized.substring(0, 1997) + '...';
    }

    return sanitized;
  }

  /**
   * Enrich input sources by fetching fresh survey responses from API
   * Backend expects input_sources array with type and data fields
   */
  private async enrichInputSourcesWithSurveyData(): Promise<StatementInputSource[]> {
    const enrichedInputs: StatementInputSource[] = [];

    for (const input of this.selectedInputs) {
      if (input.type === 'survey') {
        // Fetch fresh survey responses from API
        const surveyId = input.metadata?.surveyId || input.data?.id;

        if (surveyId) {
          try {
            const surveyData = await new Promise<any>((resolve, reject) => {
              this.surveysService.surveyResponses(
                surveyId.toString(),
                1,
                10,
                (result: any) => resolve(result),
                (error: any) => reject(error)
              );
            });

            // Enrich the input with complete survey data
            enrichedInputs.push({
              ...input,
              data: {
                ...input.data,
                // Add fetched responses and feedback
                responses: surveyData.responses || surveyData || [],
                questions: surveyData.questions || input.data?.questions || [],
                feedback: this.extractFeedbackFromResponses(surveyData.responses || surveyData || [])
              }
            });
          } catch (error) {
            console.error(`Failed to fetch responses for survey ${surveyId}:`, error);
            // Use existing data if fetch fails
            enrichedInputs.push(input);
          }
        } else {
          // No survey ID, use existing data
          enrichedInputs.push(input);
        }
      } else {
        // Non-survey inputs, use as-is
        enrichedInputs.push(input);
      }
    }

    return enrichedInputs;
  }

  /**
   * Build context object from selected inputs for the backend (DEPRECATED - backend builds context from input_sources)
   * Fetches complete survey data from API
   */
  private async buildGenerationContext_DEPRECATED(): Promise<any> {
    const context: any = {};

    // Extract project data
    const projectInput = this.selectedInputs.find(i => i.type === 'project_details');
    if (projectInput && projectInput.data) {
      context.project = {
        type: 'project_details',
        title: this.sanitizeString(projectInput.data.title),
        obiettivi: this.sanitizeString(projectInput.data.obiettivi),
        contesto_clinico_scientifico: this.sanitizeString(projectInput.data.contesto_clinico_scientifico),
        faculty: this.sanitizeString(projectInput.data.faculty),
        board: this.sanitizeString(projectInput.data.board)
      };
    }

    // Extract PICO data
    const picoInput = this.selectedInputs.find(i => i.type === 'pico');
    if (picoInput && picoInput.data) {
      context.pico = {
        type: 'pico',
        population: this.sanitizeString(picoInput.data.population),
        intervention: this.sanitizeString(picoInput.data.intervention),
        comparison: this.sanitizeString(picoInput.data.comparison),
        outcome: this.sanitizeString(picoInput.data.outcome)
      };
    }

    // Extract PubMed articles
    const pubmedInputs = this.selectedInputs.filter(i => i.type === 'pubmed');
    if (pubmedInputs.length > 0) {
      context.articles = pubmedInputs.map(input => ({
        type: 'pubmed',
        title: this.sanitizeString(input.data?.title || input.name),
        year: this.sanitizeString(input.data?.year),
        abstract: this.sanitizeString(input.data?.abstract),
        pmid: this.sanitizeString(input.data?.pmid),
        url: this.sanitizeString(input.metadata?.url)
      }));
    }

    // Extract literature reviews
    const literatureInputs = this.selectedInputs.filter(i => i.type === 'literature_review');
    if (literatureInputs.length > 0) {
      context.literature = literatureInputs.map(input => ({
        type: 'literature_review',
        summary: this.sanitizeString(input.data?.summary || input.data?.content),
        source: this.sanitizeString(input.name)
      }));
    }

    // Extract surveys with complete data (attendees, feedback, responses)
    // Fetch fresh data from API for each survey
    const surveyInputs = this.selectedInputs.filter(i => i.type === 'survey');
    if (surveyInputs.length > 0) {
      // Fetch all survey responses in parallel
      const surveyPromises = surveyInputs.map(input => {
        return new Promise((resolve, reject) => {
          const surveyId = input.metadata?.surveyId || input.data?.id;

          if (!surveyId) {
            // If no ID, use existing data
            resolve({
              type: 'survey',
              survey_id: null,
              title: this.sanitizeString(input.name),
              summary: this.sanitizeString(input.data?.summary || ''),
              responses: [],
              questions: []
            });
            return;
          }

          // Fetch complete survey responses from API
          this.surveysService.surveyResponses(
            surveyId.toString(),
            1,
            10,
            (result: any) => {
              const surveyData = input.data || {};

              resolve({
                type: 'survey',
                survey_id: surveyId,
                title: this.sanitizeString(surveyData.title || input.name),
                description: this.sanitizeString(surveyData.description),
                status: surveyData.status,

                // Attendees/Members/Participants
                attendees: surveyData.members || surveyData.participants || [],
                participants_count: surveyData.responses_count || surveyData.participants_count || 0,
                completion_rate: surveyData.completion_rate || 0,

                // Responses from API
                responses: result.responses || result || [],

                // Questions and results
                questions: result.questions || surveyData.questions || [],

                // Feedback from responses
                feedback: this.extractFeedbackFromResponses(result.responses || result || []),

                // Summary for context
                summary: this.sanitizeString(
                  surveyData.summary || this.buildSurveySummary(surveyData)
                ),

                // Metadata
                created_at: surveyData.created_at,
                closed_at: surveyData.closed_at
              });
            },
            (error: any) => {
              console.error(`Failed to fetch responses for survey ${surveyId}:`, error);
              // Fall back to existing data on error
              const surveyData = input.data || {};
              resolve({
                type: 'survey',
                survey_id: surveyId,
                title: this.sanitizeString(surveyData.title || input.name),
                description: this.sanitizeString(surveyData.description),
                summary: this.sanitizeString(surveyData.summary || ''),
                responses: surveyData.responses || [],
                questions: surveyData.questions || []
              });
            }
          );
        });
      });

      // Wait for all survey data to be fetched
      context.surveys = await Promise.all(surveyPromises);
    }

    // Extract uploaded PDF documents
    const pdfInputs = this.selectedInputs.filter(i => i.type === 'pdf');
    if (pdfInputs.length > 0) {
      context.documents = pdfInputs.map(input => {
        const content = input.data?.content || input.data?.text || `[PDF: ${input.name}]`;
        // Limit document content to 5000 chars
        return {
          type: 'pdf',
          content: this.sanitizeString(content.substring(0, 5000)),
          name: this.sanitizeString(input.name)
        };
      });
    }

    // Extract GRADE revisions
    const gradeInputs = this.selectedInputs.filter(i => i.type === 'grade');
    if (gradeInputs.length > 0) {
      context.grade = gradeInputs.map(input => {
        const summary = input.data?.summary || this.buildGradeSummary(input.data);
        return {
          type: 'grade',
          summary: this.sanitizeString(summary),
          revision_id: input.data?.id || null,
          title: this.sanitizeString(input.data?.title || input.name)
        };
      });
    }

    // Clean up empty fields to avoid sending unnecessary data
    Object.keys(context).forEach(key => {
      if (Array.isArray(context[key]) && context[key].length === 0) {
        delete context[key];
      } else if (typeof context[key] === 'object' && context[key] !== null) {
        // Check if all values in object are empty strings
        const allEmpty = Object.values(context[key]).every(val => val === '' || val === null);
        if (allEmpty) {
          delete context[key];
        }
      }
    });


    return context;
  }

  /**
   * Extract feedback from survey responses
   */
  private extractFeedbackFromResponses(responses: any[]): string[] {
    if (!responses || !Array.isArray(responses)) return [];

    const feedback: string[] = [];

    responses.forEach((response: any) => {
      // Check for feedback field directly
      if (response.feedback) {
        feedback.push(this.sanitizeString(response.feedback));
      }

      // Check for answers array with feedback
      if (response.answers && Array.isArray(response.answers)) {
        response.answers.forEach((answer: any) => {
          if (answer.feedback) {
            feedback.push(this.sanitizeString(answer.feedback));
          }
        });
      }

      // Check for text-type responses that might be feedback
      if (response.answer_value && typeof response.answer_value === 'string') {
        feedback.push(this.sanitizeString(response.answer_value));
      }
    });

    return feedback.filter(f => f && f.length > 0);
  }

  /**
   * Build a summary string from survey data
   */
  private buildSurveySummary(surveyData: any): string {
    if (!surveyData) return '';

    let summary = `Survey: ${surveyData.title || 'Untitled'}\n`;
    if (surveyData.description) {
      summary += `Description: ${surveyData.description}\n`;
    }
    if (surveyData.responses && surveyData.responses.length > 0) {
      summary += `Responses: ${surveyData.responses.length}\n`;
      summary += 'Key findings:\n';
      surveyData.responses.slice(0, 5).forEach((response: any) => {
        summary += `- ${response.question_text}: ${JSON.stringify(response.answers)}\n`;
      });
    }
    return summary;
  }

  /**
   * Build a summary string from GRADE data
   */
  private buildGradeSummary(gradeData: any): string {
    if (!gradeData) return '';

    let summary = `GRADE Revision: ${gradeData.title || 'Untitled'}\n`;
    if (gradeData.description) {
      summary += `Description: ${gradeData.description}\n`;
    }
    if (gradeData.quality) {
      summary += `Quality of Evidence: ${gradeData.quality}\n`;
    }
    if (gradeData.recommendations) {
      summary += `Recommendations:\n${gradeData.recommendations}\n`;
    }
    return summary;
  }

  /**
   * Generate statements from selected inputs
   */
  async generateStatements(): Promise<void> {

    if (this.selectedInputs.length === 0) {
      this.showMessage('statement-generator.no-inputs-selected', 'warning');
      return;
    }

    const organizationId = this.userService.getCurrentOrganization()?.id;
    if (!organizationId) {
      this.showMessage('generic.error.no-organization', 'error');
      return;
    }

    this.isGenerating = true;
    this.generationProgress = 0;
    this.generationMessage = this.translate.instant('statement-generator.preparing');

    // Enrich input sources with fresh survey data from API
    // Backend will build context from input_sources array

    const enrichedInputSources = await this.enrichInputSourcesWithSurveyData();


    const request: StatementGenerationRequest = {
      project_id: this.projectId,
      organization_id: organizationId,
      input_sources: enrichedInputSources,  // Send enriched input sources, backend builds context
      generation_options: {
        include_pico: this.selectedInputs.some(i => i.type === 'pico'),
        include_project_details: this.selectedInputs.some(i => i.type === 'project_details'),
        language: this.translate.currentLang || 'it'
      }
    };


    this.statementService.generateStatements(
      request,
      (response) => {
        this.currentProcess = {
          id: response.process_id,
          project_id: this.projectId,
          organization_id: organizationId,
          status: response.status,
          input_sources_count: this.selectedInputs.length,
          message: response.message,
          created_at: response.created_at,
          updated_at: response.created_at
        };

        // Start polling for status
        this.startPolling(response.process_id);
      },
      (error) => {
        console.error('Error generating statements:', error);
        this.isGenerating = false;
        this.showMessage('statement-generator.generation-failed', 'error');
      }
    );
  }

  /**
   * Poll for statement generation status
   */
  private startPolling(processId: string): void {
    this.generationMessage = this.translate.instant('statement-generator.processing');
    this.generationProgress = 10;

    this.pollingSubscription = this.statementService.pollProcessStatus(processId).subscribe({
      next: (process) => {
        this.currentProcess = process;
        this.generationProgress = process.progress_percentage || 50;
        this.generationMessage = process.message || this.translate.instant('statement-generator.processing');

        if (process.status === 'completed') {
          // Unsubscribe immediately to prevent multiple calls
          if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = null;
          }
          this.onGenerationCompleted(processId);
        } else if (process.status === 'failed') {
          // Unsubscribe on failure too
          if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = null;
          }
          this.onGenerationFailed(process.error_message);
        } else if (process.status === 'needs_input') {
          // Unsubscribe - waiting for user to add more data
          if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = null;
          }
          this.onNeedsInput(process.message, process.error_message);
        }
      },
      error: (error) => {
        console.error('Polling error:', error);
        if (this.pollingSubscription) {
          this.pollingSubscription.unsubscribe();
          this.pollingSubscription = null;
        }
        this.isGenerating = false;
        this.showMessage('statement-generator.polling-failed', 'error');
      }
    });
  }

  /**
   * Handle successful statement generation
   */
  private onGenerationCompleted(processId: string): void {
    this.generationProgress = 90;
    this.generationMessage = this.translate.instant('statement-generator.loading-statements');

    this.statementService.getGeneratedStatements(
      processId,
      (statements) => {
        this.generatedStatements = statements;
        this.generationProgress = 95;

        // Automatically save statements to database (only if enabled)
        if (this.enableAutoSave && this.projectId && statements.length > 0) {
          this.generationMessage = this.translate.instant('statement-generator.saving-statements');
          this.autoSaveStatements(statements);
        } else {
          this.isGenerating = false;
          this.generationProgress = 100;
          this.showMessage('statement-generator.generation-success', 'success');
        }
      },
      (error) => {
        console.error('Error loading statements:', error);
        this.isGenerating = false;
        this.showMessage('statement-generator.loading-failed', 'error');
      }
    );
  }

  /**
   * Automatically save generated statements to database
   * NOTE: This requires backend endpoint POST /api/projects/{projectId}/statements
   */
  private autoSaveStatements(statements: GeneratedStatement[]): void {
    // Set a timeout to prevent infinite hanging
    const saveTimeout = setTimeout(() => {
      console.warn('Auto-save timeout reached');
      this.isGenerating = false;
      this.generationProgress = 100;
      this.showMessage('statement-generator.generation-success-save-timeout', 'warning');
    }, 10000); // 10 second timeout

    this.statementService.saveStatementsToProject(
      this.projectId,
      statements,
      (result) => {
        clearTimeout(saveTimeout);

        // Update statements with IDs from database if returned
        if (result && result.statements) {
          this.generatedStatements = result.statements;
        }

        this.isGenerating = false;
        this.generationProgress = 100;
        this.showMessage('statement-generator.generation-and-save-success', 'success');
      },
      (error) => {
        clearTimeout(saveTimeout);
        console.error('Error auto-saving statements:', error);
        this.isGenerating = false;
        this.generationProgress = 100;
        // Still show success for generation, but warn about save failure
        this.showMessage('statement-generator.generation-success-save-failed', 'warning');
      }
    );
  }

  /**
   * Handle failed statement generation
   */
  private onGenerationFailed(errorMessage?: string): void {
    this.isGenerating = false;
    this.generationProgress = 0;
    const message = errorMessage || this.translate.instant('statement-generator.generation-failed');
    this.showMessage(message, 'error');
  }

  /**
   * Handle 'needs_input' status - AI needs more data to generate statements
   */
  private onNeedsInput(message?: string, errorMessageJson?: string): void {
    this.isGenerating = false;
    this.generationProgress = 0;
    this.needsInput = true;

    // Parse error_message JSON to extract details and suggestions
    if (errorMessageJson) {
      try {
        const errorData = JSON.parse(errorMessageJson);
        this.needsInputMessage = errorData.message || message || this.translate.instant('statement-generator.needs-more-data');
        this.needsInputSuggestions = errorData.suggestions || [];
      } catch (e) {
        console.error('Failed to parse error_message JSON:', e);
        this.needsInputMessage = message || this.translate.instant('statement-generator.needs-more-data');
        this.needsInputSuggestions = [];
      }
    } else {
      this.needsInputMessage = message || this.translate.instant('statement-generator.needs-more-data');
      this.needsInputSuggestions = [];
    }


    // Show informational message (not error - it's a request for more data)
    this.showMessage(this.needsInputMessage, 'warning');
  }

  /**
   * Retry generation after adding more data
   */
  retryGeneration(): void {
    this.needsInput = false;
    this.needsInputMessage = '';
    this.needsInputSuggestions = [];
    // User can now add more sources and click generate again
  }

  /**
   * Remove an input source
   */
  removeInput(index: number): void {
    const input = this.selectedInputs[index];

    // Prevent removal of auto-added context
    if (input.type === 'project_details' || input.type === 'pico') {
      this.showMessage('statement-generator.cannot-remove-context', 'warning');
      return;
    }

    this.selectedInputs.splice(index, 1);
    this.generatedStatements = [];
  }

  /**
   * Clear all selected inputs (except project context)
   */
  clearAllInputs(): void {
    // Keep project_details and pico
    this.selectedInputs = this.selectedInputs.filter(
      input => input.type === 'project_details' || input.type === 'pico'
    );
    this.generatedStatements = [];
  }

  /**
   * Get icon for input type
   */
  getInputIcon(type: string): string {
    const icons: { [key: string]: string } = {
      'survey': 'poll',
      'pdf': 'picture_as_pdf',
      'pubmed': 'article',
      'pico': 'psychology',
      'project_details': 'folder',
      'literature_review': 'library_books'
    };
    return icons[type] || 'insert_drive_file';
  }

  /**
   * Get badge class for Level of Evidence
   */
  getLoEBadgeClass(loe: string): string {
    const level = loe.toLowerCase();
    if (level === '1a') return 'loe-1a';
    if (level === '1b') return 'loe-1b';
    if (level === '2a') return 'loe-2a';
    if (level === '2b') return 'loe-2b';
    return 'loe-3';
  }

  /**
   * Get badge class for Grade of Recommendation
   */
  getGoRBadgeClass(gor: string): string {
    const grade = gor.toUpperCase();
    return `gor-${grade.toLowerCase()}`;
  }

  /**
   * Load stored statements from database
   */
  loadStoredStatements(): void {
    if (!this.projectId) {
      return;
    }

    this.statementService.getProjectStatements(
      this.projectId,
      (statements) => {
        if (statements && statements.length > 0) {
          this.generatedStatements = statements;
        }
      },
      (error) => {
        console.error('Error loading stored statements:', error);
        // Don't show error message on init - statements might not exist yet
      }
    );
  }

  /**
   * Load all draft survey statements for this project (Lambda-generated) and show table
   */
  loadProjectDraftStatements(): void {
    if (!this.projectId) return;

    this.statementService.getProjectDraftStatements(
      this.projectId,
      (response) => {
        if (response.statements && response.statements.length > 0) {
          this.newGenerationState.statements = response.statements;
          // Don't steal the view from an in-progress generation that we are
          // re-attaching after a reload (see resumeActiveGenerationIfAny).
          if (!this.showProgressMonitor) {
            this.showStatementReview = true;
            this.selectedTabIndex = 3; // Switch to "Statement Generati" tab
          }
        }
      },
      (error) => {
        console.error('Error loading project draft statements:', error);
      }
    );
  }

  /**
   * Save statements to project (manual save)
   */
  saveStatements(): void {
    if (!this.projectId || this.generatedStatements.length === 0) {
      return;
    }

    this.statementService.saveStatementsToProject(
      this.projectId,
      this.generatedStatements,
      (result) => {
        this.showMessage('statement-generator.statements-saved', 'success');
        // Reload to get updated IDs
        this.loadStoredStatements();
      },
      (error) => {
        console.error('Error saving statements:', error);
        this.showMessage('statement-generator.save-failed', 'error');
      }
    );
  }

  /**
   * Update a specific statement
   */
  updateStatement(statement: GeneratedStatement, updates: Partial<GeneratedStatement>): void {
    if (!statement.id) {
      console.error('Cannot update statement without ID');
      return;
    }

    this.statementService.updateStatement(
      statement.id,
      updates,
      (updatedStatement) => {
        // Update in local array
        const index = this.generatedStatements.findIndex(s => s.id === statement.id);
        if (index !== -1) {
          this.generatedStatements[index] = updatedStatement;
        }
        this.showMessage('statement-generator.statement-updated', 'success');
      },
      (error) => {
        console.error('Error updating statement:', error);
        this.showMessage('statement-generator.update-failed', 'error');
      }
    );
  }

  /**
   * Delete a statement
   */
  deleteStatement(statement: GeneratedStatement): void {
    // Confirm deletion
    const statementPreview = statement.statement_text?.substring(0, 50) || 'questo statement';
    const confirmMessage = `Sei sicuro di voler eliminare questo statement?\n\n"${statementPreview}${statement.statement_text && statement.statement_text.length > 50 ? '...' : ''}"\n\nQuesta azione non può essere annullata.`;

    if (!confirm(confirmMessage)) {
      return;
    }

    // If it's an unsaved statement (no ID from database)
    if (!statement.id && statement.id !== 0) {
      // Just remove from local array

      this.generatedStatements = this.generatedStatements.filter(s => s !== statement);
      this.showMessage('Statement rimosso dalla lista', 'success');
      return;
    }

    // Delete from database

    this.statementService.deleteStatement(
      statement.id,
      (result) => {
        // Remove from local array
        const previousCount = this.generatedStatements.length;
        this.generatedStatements = this.generatedStatements.filter(s => s.id !== statement.id);

        this.showMessage('Statement eliminato con successo', 'success');
      },
      (error) => {
        console.error('Error deleting statement:', error);
        this.showMessage('Errore durante l\'eliminazione dello statement', 'error');
      }
    );
  }

  /**
   * Prompt user for AI-assisted update
   */
  promptAIUpdate(statement: GeneratedStatement): void {
    const promptMessage = this.translate.instant('statement-generator.ai-update-prompt-message');
    const updatePrompt = window.prompt(promptMessage);

    if (updatePrompt && updatePrompt.trim().length > 0) {
      this.updateStatementWithAI(statement, updatePrompt);
    }
  }

  /**
   * Update statement with AI assistance
   */
  updateStatementWithAI(statement: GeneratedStatement, updatePrompt: string): void {
    if (!statement.id) {
      console.error('Cannot update statement without ID');
      this.showMessage('statement-generator.no-id-error', 'error');
      return;
    }

    if (!updatePrompt || updatePrompt.trim().length === 0) {
      this.showMessage('statement-generator.empty-prompt-error', 'error');
      return;
    }

    // Show loading state
    this.showMessage('statement-generator.ai-updating', 'success');

    this.statementService.updateStatementWithAI(
      statement.id,
      updatePrompt,
      (updatedStatement) => {
        // Update in local array
        const index = this.generatedStatements.findIndex(s => s.id === statement.id);
        if (index !== -1) {
          this.generatedStatements[index] = updatedStatement;
        }
        this.showMessage('statement-generator.ai-update-success', 'success');
      },
      (error) => {
        console.error('Error updating statement with AI:', error);
        this.showMessage('statement-generator.ai-update-failed', 'error');
      }
    );
  }

  /**
   * Show snackbar message
   */
  private showMessage(messageKey: string, type: 'success' | 'error' | 'warning'): void {
    const message = this.translate.instant(messageKey);
    this.snackBar.open(message, '', {
      duration: 3000,
      panelClass: [`snackbar-${type}`],
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }

  /**
   * Check if has any user-selected inputs (excluding context)
   */
  get hasUserInputs(): boolean {
    return this.selectedInputs.some(
      input => input.type !== 'project_details' && input.type !== 'pico'
    );
  }

  /**
   * Get input type label
   */
  getInputTypeLabel(type: string): string {
    const labels: { [key: string]: string } = {
      'survey': this.translate.instant('statement-generator.input-types.survey'),
      'pdf': this.translate.instant('statement-generator.input-types.pdf'),
      'pubmed': this.translate.instant('statement-generator.input-types.pubmed'),
      'pico': this.translate.instant('statement-generator.input-types.pico'),
      'project_details': this.translate.instant('statement-generator.input-types.project-details'),
      'literature_review': this.translate.instant('statement-generator.input-types.literature-review')
    };
    return labels[type] || type;
  }

  /**
   * Handle structured form submission
   */
  onFormSubmitted(formData: any): void {
    // New flow: input form → Section Whisperer → user review → V2 generator.
    // The form now kicks off the Section Whisperer; statement generation only
    // happens after the user reviews the sections (confirmSectionsAndGenerate).
    this.startSectionWhisperer(formData);
  }

  /**
   * Step 1 — start the Section Whisperer pipeline with the generator-shaped
   * payload plus n_sections, then poll until it returns the section list.
   */
  private startSectionWhisperer(formData: any): void {
    const organizationId = this.userService.getCurrentOrganization()?.id;
    if (!organizationId) {
      this.showMessage('generic.error.no-organization', 'error');
      this.formRef?.resetSubmissionState();
      return;
    }

    // Keep the payload around: it is reused verbatim for statement generation
    // once the sections are confirmed.
    this.pendingFormData = formData;

    this.sectionState = {
      processId: null,
      status: 'waiting',
      progress: 0,
      message: 'Avvio generazione sezioni...',
      error: null,
      sections: []
    };

    const request: any = {
      ...formData,
      n_sections: formData.n_sections ?? 4
    };

    this.showSectionProgress = true;
    this.showSectionReview = false;
    this.showProgressMonitor = false;
    this.showStatementReview = false;

    this.statementService.initiateSectionWhisperer(
      request,
      (response) => {
        this.sectionState.processId = response.process_id;
        this.sectionState.status = response.status;
        this.sectionState.message = response.message;
        this.pollSectionWhisperer(response.process_id);
      },
      (error) => {
        const errorMsg = error.error?.message || 'Errore durante l\'avvio della generazione delle sezioni';
        this.sectionState.status = 'failed';
        this.sectionState.error = errorMsg;
        this.showSectionProgress = false;
        this.formRef?.resetSubmissionState();
        this.showMessage(errorMsg, 'error');
      }
    );
  }

  /**
   * Poll the Section Whisperer process. On completion, populate the editable
   * section list and switch to the review step.
   */
  private pollSectionWhisperer(processId: string): void {
    this.sectionPollingSubscription?.unsubscribe();

    let cancelled = false;
    let delay = 2000;
    const startedAt = Date.now();
    const maxDurationMs = 15 * 60 * 1000;

    const tick = () => {
      if (cancelled) return;
      if (Date.now() - startedAt >= maxDurationMs) {
        this.sectionState.status = 'failed';
        this.sectionState.error = 'Timed out waiting for sections';
        this.showSectionProgress = false;
        this.formRef?.resetSubmissionState();
        this.showMessage('statement-generator.polling-failed', 'error');
        return;
      }

      this.statementService.checkSectionWhispererStatus(
        processId,
        (status: SectionWhispererStatusResponse) => {
          if (cancelled) return;
          this.sectionState.status = status.status;
          this.sectionState.progress = status.progress_percentage;
          this.sectionState.message = status.message;
          if (status.error_message) {
            this.sectionState.error = status.error_message;
          }

          if (status.status === 'completed') {
            this.sectionState.sections = status.sections || [];
            this.editableSections = [...(status.sections || [])];
            this.showSectionProgress = false;
            this.showSectionReview = true;
            // Stay on the guided-compilation tab (index 2) — that is where the
            // form and the section-review card render in this template.
            this.selectedTabIndex = 2; // surface the review step
            this.formRef?.resetSubmissionState();
            return;
          }

          if (status.status === 'failed') {
            this.showSectionProgress = false;
            this.formRef?.resetSubmissionState();
            this.showMessage('statement-generator.generation-failed', 'error');
            return;
          }

          delay = Math.min(delay * 1.5, 15000);
          setTimeout(tick, delay);
        },
        (error) => {
          if (cancelled) return;
          this.sectionState.status = 'failed';
          this.showSectionProgress = false;
          this.formRef?.resetSubmissionState();
          this.showMessage('statement-generator.polling-failed', 'error');
        }
      );
    };

    // Emulate an unsubscribable handle so ngOnDestroy / re-runs can cancel.
    this.sectionPollingSubscription = new Subscription(() => { cancelled = true; });
    setTimeout(tick, delay);
  }

  // ── Section review edit actions ───────────────────────────────────────────

  addSection(): void {
    const title = (this.newSectionTitle || '').trim();
    if (!title) return;
    this.editableSections = [...this.editableSections, title];
    this.newSectionTitle = '';
  }

  updateSection(index: number, value: string): void {
    if (index < 0 || index >= this.editableSections.length) return;
    const next = [...this.editableSections];
    next[index] = value;
    this.editableSections = next;
  }

  removeSection(index: number): void {
    this.editableSections = this.editableSections.filter((_, i) => i !== index);
  }

  /**
   * Step 2 — the user confirmed the reviewed sections: generate statements,
   * constrained to those sections. Blank titles are dropped; if nothing is
   * left the generator runs without the (optional) section constraint.
   */
  confirmSectionsAndGenerate(): void {
    if (!this.pendingFormData) {
      this.showMessage('statement-generator.generation-failed', 'error');
      return;
    }
    const selected = this.editableSections
      .map(s => (s || '').trim())
      .filter(s => s !== '');

    this.showSectionReview = false;
    this.generateStatementsFromForm(this.pendingFormData, selected);
  }

  /** Abandon the section-review step and return to the input form. */
  cancelSectionReview(): void {
    this.sectionPollingSubscription?.unsubscribe();
    this.showSectionReview = false;
    this.showSectionProgress = false;
    this.sectionState.sections = [];
    this.editableSections = [];
    this.pendingFormData = null;
    this.selectedTabIndex = 2; // back to the input form tab
  }

  /**
   * Handle PICO imported from Compilazione Guidata — sync back to PICO tab fields
   */
  onPicoImported(picoData: any): void {
    this.patients = picoData.population || '';
    this.intervention = picoData.intervention || '';
    this.comparison = picoData.comparison || '';
    this.outcomes = picoData.outcomes || '';
    this.studies = picoData.setting || picoData.studies || this.studies || '';
    this.timeframe = picoData.timeframe || this.timeframe || '';
  }

  /**
   * Handle form cancellation
   */
  onFormCanceled(): void {
    console.log('Form canceled');
    // Optionally navigate away or reset
  }

  /**
   * Handle draft save
   */
  onDraftSaved(formData: any): void {
    console.log('Draft saved:', formData);
    // TODO: Implement draft persistence to backend
    this.showMessage('Bozza salvata con successo', 'success');
  }

  /**
   * Convert structured form data to input sources
   */
  private convertFormDataToInputSources(formData: any): void {
    // Clear existing inputs except project context
    this.selectedInputs = this.selectedInputs.filter(
      input => input.type === 'project_details' || input.type === 'pico'
    );

    // Update project details with form data
    const projectDetailsInput = this.selectedInputs.find(i => i.type === 'project_details');
    if (projectDetailsInput && projectDetailsInput.data) {
      projectDetailsInput.data = {
        ...projectDetailsInput.data,
        surveyObjective: formData.surveyObjective,
        sessionFocus: formData.sessionFocus,
        consultationType: formData.consultationType,
        targetAudience: formData.targetAudience
      };
    }

    // Update PICO with form data
    const picoInput = this.selectedInputs.find(i => i.type === 'pico');
    if (picoInput && picoInput.data) {
      picoInput.data = {
        ...picoInput.data,
        ...formData.pico
      };
    }

    // Add clinical context as input
    if (formData.clinicalContext) {
      this.selectedInputs.push({
        id: 'clinical-context',
        type: 'project_details',
        name: 'Contesto Clinico',
        data: formData.clinicalContext,
        metadata: {
          uploadDate: new Date()
        }
      });
    }

    // Add anchor studies as literature references
    if (formData.evidence?.anchorStudies?.length > 0) {
      formData.evidence.anchorStudies.forEach((study: any, index: number) => {
        this.selectedInputs.push({
          id: `anchor-study-${index}`,
          type: 'literature_review',
          name: `Anchor Study ${index + 1}: ${study.reference}`,
          data: {
            reference: study.reference,
            relevance: study.relevance,
            type: 'anchor_study'
          },
          metadata: {
            uploadDate: new Date()
          }
        });
      });
    }

  }

  /**
   * Load previous surveys for the form
   */
  private loadPreviousSurveys(): void {
    if (!this.projectId) return;



    // Use SurveyService to get surveys by project ID
    this.surveysService.getSurveysByProjectId(
      this.projectId.toString(),
      (result: any) => {
        // Handle different response formats
        const surveys = result.surveys || result.data || result || [];
        this.previousSurveysAvailable = Array.isArray(surveys) ? surveys : [];



      },
      (error: any) => {
        console.error('❌ Error loading previous surveys:', error);
        this.previousSurveysAvailable = [];
      }
    );
  }

  /**
   * Load existing PICOs for the project from GRADE revisions
   */
  private loadExistingPicos(): void {
    if (!this.projectId) return;

    this.existingPicos = [];

    // Check if projectData has picos
    if (this.projectData && this.projectData.picos && Array.isArray(this.projectData.picos)) {
      this.projectData.picos.forEach((pico: any) => {
        this.existingPicos.push(pico);
      });

    }

    // Load PICOs from GRADE revisions
    this.gradeService.getRevisionsByProjectId(this.projectId).subscribe({
      next: (revisions) => {
        if (!revisions || revisions.length === 0) {
          console.log('No GRADE revisions found for PICOs');
          return;
        }



        // Extract PICOs from revisions
        revisions.forEach((revision) => {
          let picoParams: any = null;

          // Primary: Try to extract from description field (PICO_COMPONENTS format)
          if (revision.description) {
            try {
              const descriptionData = JSON.parse(revision.description);
              if (descriptionData.type === 'PICO_COMPONENTS' && descriptionData.data) {
                picoParams = descriptionData.data;
              }
            } catch (e) {
              // Description is not JSON or not PICO format
            }
          }

          // Fallback: Try search_parameters (older revisions)
          if (!picoParams && revision.search_parameters && typeof revision.search_parameters === 'object') {
            const params = revision.search_parameters;
            if (params.patients || params.intervention || params.comparison || params.outcomes) {
              picoParams = params;
            }
          }

          // If we have structured PICO data, create a proper PICO entry
          if (picoParams) {
            this.existingPicos.push({
              id: `grade-${revision.id}`,
              name: revision.title || `GRADE Revision ${revision.id}`,
              grade_revision_id: revision.id,
              // Map from patients/outcomes to population/outcome
              population: picoParams.patients || '',
              intervention: picoParams.intervention || '',
              comparator: picoParams.comparison || '',
              comparison: picoParams.comparison || '',
              outcome: picoParams.outcomes || '',
              studies: picoParams.studies || '',
              timeframe: picoParams.timeframe || '',
              pico_question: revision.pico_question || '',
              keywords: revision.keywords || '',
              source: 'grade_revision'
            });
          } else if (revision.pico_question && revision.pico_question.trim()) {
            // Fallback: Use pico_question string if no structured data
            this.existingPicos.push({
              id: `grade-${revision.id}`,
              name: revision.title || `GRADE Revision ${revision.id}`,
              grade_revision_id: revision.id,
              pico_question: revision.pico_question,
              // Put pico_question in population field so it's displayed in the dropdown
              population: revision.pico_question,
              intervention: '',
              comparator: '',
              outcome: '',
              keywords: revision.keywords || '',
              source: 'grade_revision'
            });
          }
        });

      },
      error: (error) => {
        console.error('Error loading PICOs from GRADE revisions:', error);
      }
    });
  }

  /**
   * Load existing literature/documents for the project from GRADE revisions
   */
  private loadExistingLiterature(): void {
    if (!this.projectId) return;



    // Step 1: Get all GRADE revisions for the project using GradeService
    this.gradeService.getRevisionsByProjectId(this.projectId).subscribe({
      next: (revisions) => {
        if (!revisions || revisions.length === 0) {
          // this.loadPubMedDocuments();
          return;
        }

        this.existingLiterature = [];

        // Step 2: For each GRADE revision, get all documents
        let processedRevisions = 0;
        const totalRevisions = revisions.length;

        revisions.forEach((revision, index) => {

          // Documents not included in revision response, fetch them separately
          this.gradeService.getDocuments({ grade_revision_id: revision.id }).subscribe({
            next: (documents) => {
              if (documents && documents.length > 0) {
                // Add grade revision info to each document
                documents.forEach((doc) => {
                  this.existingLiterature.push({
                    ...doc,
                    grade_revision_id: revision.id,
                    grade_revision_title: revision.title
                  });
                });
              } else {
                console.log(`  No documents found for GRADE revision ${revision.id}`);
              }
              processedRevisions++;

              if (processedRevisions === totalRevisions) {
                this.finalizeLiteratureLoading();
              }
            },
            error: (error) => {
              console.error(`  Error fetching documents for GRADE revision ${revision.id}:`, error);
              processedRevisions++;

              if (processedRevisions === totalRevisions) {
                this.finalizeLiteratureLoading();
              }
            }
          });
        });
      },
      error: (error) => {
        console.error('Error loading GRADE revisions:', error);
        // this.loadPubMedDocuments();
      }
    });
  }

  /**
   * Finalize literature loading after all GRADE revisions processed
   */
  private finalizeLiteratureLoading(): void {
    if (this.existingLiterature.length > 0) {
      // console.log(`Total literature loaded: ${this.existingLiterature.length} documents from GRADE revisions`);
    } else {
      // console.log('No documents found in GRADE revisions, trying PubMed fallback');
      // this.loadPubMedDocuments();
    }
  }

  /**
   * Group literature by GRADE revision for logging
   */
  private groupLiteratureByGrade(): any {
    const grouped: any = {};
    this.existingLiterature.forEach((doc: any) => {
      const gradeId = doc.grade_revision_id || 'unknown';
      if (!grouped[gradeId]) {
        grouped[gradeId] = {
          title: doc.grade_revision_title || `GRADE ${gradeId}`,
          count: 0
        };
      }
      grouped[gradeId].count++;
    });
    return grouped;
  }

  /**
   * Load PubMed documents as fallback
   */
  // private loadPubMedDocuments(): void {
  //   if (!this.projectId) return;

  //   // console.log('Loading PubMed documents as fallback');
  //   this.statementService.getProjectPubMedDocuments(
  //     this.projectId,
  //     (documents) => {
  //       this.existingLiterature = documents || [];
  //       // console.log(`Loaded ${this.existingLiterature.length} PubMed documents`);
  //     },
  //     (error) => {
  //       // console.error('Error loading PubMed documents:', error);
  //       this.existingLiterature = [];
  //     }
  //   );
  // }

  // =============================================================================
  // Scientific Papers Upload Methods
  // =============================================================================

  /**
   * Open modal to upload scientific papers
   */
  openUploadPapersModal(attachmentType: 'FILE' | 'PUBMED'): void {
    const organizationId = this.userService.getCurrentOrganization()?.id;
    if (!organizationId) {
      this.snackBar.open('Errore: nessuna organizzazione selezionata', 'Chiudi', { duration: 3000 });
      return;
    }

    if (!this.projectId) {
      this.snackBar.open('Errore: nessun progetto selezionato', 'Chiudi', { duration: 3000 });
      return;
    }

    const uploadUrl = `upload/organization-file/${organizationId}/${this.projectId}`;

    const dialogRef = this.dialog.open(ModalAddAttachmentComponent, {
      panelClass: 'large-dialog',
      data: {
        title: attachmentType === 'PUBMED' ? 'Aggiungi articolo da PubMed' : 'Carica articoli scientifici',
        attachmentType: attachmentType,
        uploadUrl: uploadUrl,
        acceptedTypes: '.pdf,application/pdf'
      }
    });

    dialogRef.afterClosed().subscribe((attachmentData) => {
      if (attachmentData) {
        if (attachmentType === 'PUBMED' && attachmentData.attachmentObject) {
          const articles = Array.isArray(attachmentData.attachmentObject)
            ? attachmentData.attachmentObject
            : [attachmentData.attachmentObject];

          articles.forEach((pubmedData) => {
            const exists = this.uploadedPapers.some(p => p.PMID === pubmedData.PMID);

            if (!exists) {
              // Extract DOI from various possible fields
              const doi = pubmedData.Doid || pubmedData.DOI || pubmedData.doi || null;

              const obj = {
                type: 'PUBMED',
                PMID: pubmedData.PMID,
                title: pubmedData.ArticleTitle,
                link: pubmedData.link,
                doi: doi,
                authors: pubmedData.Authors || null,
                year: pubmedData.PubYear || null,
                study_type: null,
                main_outcome: null,
                llm_analysis: null,
                created_at: new Date().toISOString(),
                status: 'waiting'
              };
              this.uploadedPapers.push(obj);

              // Save PubMed document to database immediately
              this.aiService.savePubmedDocument(
                organizationId,
                this.projectId,
                pubmedData,
                (response) => {
                  const paper = this.uploadedPapers.find(p => p.PMID === pubmedData.PMID);
                  if (paper) {
                    paper.id           = response.id || response.file_id;
                    paper.status       = response.status       || 'waiting';
                    paper.doi          = response.doi          || response.Doid  || paper.doi;
                    paper.authors      = response.authors      || paper.authors;
                    paper.year         = response.year         || paper.year;
                    paper.study_type   = response.study_type   || paper.study_type;
                    paper.main_outcome = response.main_outcome || paper.main_outcome;
                    paper.llm_analysis = response.llm_analysis || paper.llm_analysis;
                  }
                  this.snackBar.open('Articolo PubMed salvato con successo', 'Chiudi', { duration: 3000 });
                },
                (error) => {
                  console.error('Error saving PubMed document:', error);
                  const paper = this.uploadedPapers.find(p => p.PMID === pubmedData.PMID);
                  if (paper) {
                    paper.status = 'failed';
                    paper.reason = error.error?.message || 'Errore durante il salvataggio';
                  }
                  this.snackBar.open('Errore durante il salvataggio dell\'articolo PubMed', 'Chiudi', { duration: 3000 });
                }
              );
            }
          });
        } else if (attachmentType === 'FILE' && attachmentData.attachmentObject && attachmentData.attachmentObject.length > 0) {
          // Handle file attachments
          attachmentData.attachmentObject.forEach((file: any) => {
            const exists = this.uploadedPapers.some(p => p.id === file.id);
            if (!exists) {
              const obj = {
                ...file,
                type: 'FILE',
                created_at: new Date().toISOString(),
                isNew: true,
                status: 'waiting'
              };
              this.uploadedPapers.push(obj);
            }
          });

          this.snackBar.open(
            `${attachmentData.attachmentObject.length} documento/i caricato/i con successo`,
            'Chiudi',
            { duration: 3000 }
          );
        }
      }
    });
  }

  exportLiteratureToExcel(): void {
    const rows = this.uploadedPapers.map(p => ({
      'Type': p.type || '',
      'Authors': p.authors || '',
      'Title': p.title || '',
      'Year': p.year || p.pubyear || '',
      'Study Type': p.study_type || '',
      'Main Outcome': p.main_outcome || '',
      'LLM Analysis': p.llm_analysis || '',
      'Link': p.link || '',
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Literature');

    const projectTitle = this.projectData?.title || 'project';
    const safeName = projectTitle.replace(/[^a-z0-9_\-]/gi, '_').toLowerCase();
    XLSX.writeFile(wb, `literature_${safeName}.xlsx`);
  }

  /**
   * Documenti caricati filtrati per testo (titolo, autori, anno, PMID, DOI, tipo studio, outcome, stato)
   */
  get filteredPapers(): any[] {
    const term = this.papersFilterText.trim().toLowerCase();
    if (!term) return this.uploadedPapers;
    return this.uploadedPapers.filter(paper =>
      [paper.title, paper.name, paper.authors, paper.year, paper.pubyear, paper.PMID, paper.pmid, paper.doi,
        paper.type, paper.study_type, paper.main_outcome, paper.status]
        .some(value => value != null && String(value).toLowerCase().includes(term))
    );
  }

  /**
   * Remove a paper from the list — calls backend to delete the record and its LLM analysis
   */
  // Papers selected in the literature table (kept by reference: indexes shift on delete)
  selectedPapers = new Set<any>();
  deletingSelectedPapers = false;

  get allPapersSelected(): boolean {
    const visible = this.filteredPapers;
    return visible.length > 0 && visible.every(p => this.selectedPapers.has(p));
  }

  get selectedPapersCount(): number {
    return this.uploadedPapers.filter(p => this.selectedPapers.has(p)).length;
  }

  get somePapersSelected(): boolean {
    return this.selectedPapersCount > 0 && !this.allPapersSelected;
  }

  togglePaperSelection(paper: any, checked: boolean): void {
    if (checked) {
      this.selectedPapers.add(paper);
    } else {
      this.selectedPapers.delete(paper);
    }
  }

  toggleAllPapers(checked: boolean): void {
    this.filteredPapers.forEach(p => checked ? this.selectedPapers.add(p) : this.selectedPapers.delete(p));
    this.selectedPapers = new Set(this.selectedPapers);
  }

  /**
   * Delete the selected papers in one backend call (files and PubMed together).
   * Papers with no backend record yet are only removed locally.
   */
  removeSelectedPapers(): void {
    const selected = this.uploadedPapers.filter(p => this.selectedPapers.has(p));
    if (!selected.length) return;
    if (!confirm(`Eliminare ${selected.length} documenti selezionati?`)) return;

    const persisted = selected.filter(p => p.id);
    const removeLocally = (papers: any[]) => {
      this.uploadedPapers = this.uploadedPapers.filter(p => !papers.includes(p));
      papers.forEach(p => this.selectedPapers.delete(p));
    };

    removeLocally(selected.filter(p => !p.id));
    if (!persisted.length) return;

    this.deletingSelectedPapers = true;
    this.aiService.bulkDeleteLiteratureAttachments(
      this.projectId,
      persisted.map(p => ({ type: p.type === 'PUBMED' ? 'PUBMED' : 'FILE', id: p.id })),
      (result) => {
        // Not found = already gone on the backend: remove those too
        const gone = new Set([...(result?.deleted || []), ...(result?.not_found || [])]
          .map((d: any) => `${d.type}:${d.id}`));
        removeLocally(persisted.filter(p => gone.has(`${p.type === 'PUBMED' ? 'PUBMED' : 'FILE'}:${p.id}`)));
        this.deletingSelectedPapers = false;
        this.snackBar.open(`${result?.deleted?.length ?? 0} documenti rimossi`, 'Chiudi', { duration: 3000 });
      },
      (error) => {
        console.error('Error deleting selected attachments:', error);
        this.deletingSelectedPapers = false;
        this.snackBar.open('Errore durante la rimozione dei documenti', 'Chiudi', { duration: 3000 });
      }
    );
  }

  /**
   * Open dialog to view full AI analysis or error details
   */
  openAnalysisDialog(paperTitle: string, content: string, isError: boolean = false): void {
    const dialogRef = this.dialog.open(AnalysisDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '85vh',
      panelClass: 'analysis-dialog-container',
      data: {
        title: paperTitle,
        content: content,
        isError: isError
      }
    });
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Extract only the Summary section from the full AI analysis
   * Handles different formats: string, object with policy/causal_map split, etc.
   * @param fullAnalysis - The complete AI analysis (string or object)
   * @returns Only the summary section or combined analysis
   */
  private extractSummaryFromAnalysis(fullAnalysis: any): string | null {

    if (!fullAnalysis) {
      return null;
    }

    // If it's an object (e.g., {policy: "...", causal_map: "..."})
    if (typeof fullAnalysis === 'object' && !Array.isArray(fullAnalysis)) {
      const parts: string[] = [];

      // Check for policy field
      if (fullAnalysis.policy) {
        parts.push('**Policy Analysis:**\n' + fullAnalysis.policy);
      }

      // Check for causal_map field
      if (fullAnalysis.causal_map) {
        parts.push('**Causal Map:**\n' + fullAnalysis.causal_map);
      }

      // Check for summary field
      if (fullAnalysis.summary) {
        parts.push('**Summary:**\n' + fullAnalysis.summary);
      }

      // If no recognized fields, try to stringify the object
      if (parts.length === 0) {
        const result = JSON.stringify(fullAnalysis, null, 2);
        return result;
      }

      const result = parts.join('\n\n');
      return result;
    }

    // If it's a string, use the original logic
    if (typeof fullAnalysis === 'string') {
      // Find the start of the summary section
      const summaryMarker = '## Summary of the Causal Model';
      const summaryIndex = fullAnalysis.indexOf(summaryMarker);

      // If summary section is not found, return the full analysis
      if (summaryIndex === -1) {
        return fullAnalysis;
      }

      // Extract everything from the summary marker to the end
      const result = fullAnalysis.substring(summaryIndex).trim();
      return result;
    }

    // Fallback: try to convert to string
    return String(fullAnalysis);
  }

  /**
   * Load project attachments and check their processing status
   */
  loadProjectAttachments(): void {
    if (!this.projectId) {
      console.warn('No project ID available');
      return;
    }


    // The status endpoint returns both attachments and their status
    // Pass true to start polling if documents are in processing status
    this.checkAttachmentsStatus(true);
  }

  /**
   * Check processing status for attachments
   * @param startPollingIfNeeded - If true, will start polling if documents are in processing status
   * @param onAfterUpdate - Optional callback invoked after uploadedPapers is updated
   */
  checkAttachmentsStatus(startPollingIfNeeded: boolean = false, onAfterUpdate?: () => void): void {
    if (!this.projectId) return;

    this.aiService.checkLiteratureStatus(
      this.projectId,
      (statusResponse: any) => {

        // The response contains attachments with status already included
        const attachmentsFromResponse = statusResponse.attachments || [];

        // Log each attachment to see what fields are available
        attachmentsFromResponse.forEach((att: any, index: number) => {
          const hasAnalysis = !!(att.llm_analysis || att.analysis || att.ai_analysis);

          // console.log(`Attachment ${index} (${att.type}):`, {
          //   id: att.id,
          //   title: att.title?.substring(0, 50),
          //   type: att.type,
          //   status: att.status,
          //   hasAnalysis: hasAnalysis,
          //   llm_analysis_exists: !!att.llm_analysis,
          //   allKeys: Object.keys(att)
          // });

          // CRITICAL: Backend issue - PUBMED papers don't have llm_analysis
          if (att.type === 'PUBMED' && !hasAnalysis && att.status === 'completed') {
            console.error('BACKEND ISSUE: PUBMED paper has status=completed but NO llm_analysis!', {
              id: att.id,
              title: att.title,
              PMID: att.PMID,
              endpoint: `ai/ai-analysis/process-literature-status/${this.projectId}`,
              issue: 'Backend returns llm_analysis for FILE but NOT for PUBMED',
              solution: 'Backend needs to process PUBMED papers and include llm_analysis in response'
            });
          }
        });

        // Map the attachments to the uploadedPapers format
        this.uploadedPapers = attachmentsFromResponse.map((attachment: any) => {

          // Extract analysis
          let extractedAnalysis = this.extractSummaryFromAnalysis(attachment.llm_analysis);

          // BACKEND FIX: Handle PUBMED documents where llm_analysis is boolean true
          if (!extractedAnalysis && typeof attachment.llm_analysis === 'boolean' && attachment.llm_analysis === true) {
            // Use abstract as fallback for PUBMED documents
            extractedAnalysis = attachment.abstract || 'Analysis available but not returned by backend';
          }

          const paper = {
            id: attachment.id,
            title: attachment.title,
            type: attachment.type,
            size: parseInt(attachment.size) || 0,
            status: attachment.status || 'waiting',
            reason: attachment.reason || null,
            llm_analysis: extractedAnalysis,
            doi: attachment.Doid || attachment.doi || null,
            PMID: attachment.PMID || attachment.pmid || null,
            authors: attachment.authors || null,
            year: attachment.year || null,
            study_type: attachment.study_type || null,
            main_outcome: attachment.main_outcome || null,
            abstract: attachment.abstract || null,
            link: attachment.link,
            path: attachment.path,
            ext: attachment.ext,
            createdAt: attachment.created_at,
            updatedAt: attachment.updated_at
          };

          return paper;
        });

        // Only start polling on initial load, not during polling itself
        if (startPollingIfNeeded) {
          const hasProcessing = this.uploadedPapers.some(p => p.status === 'processing');

          if (hasProcessing) {
            this.isProcessingPapers = true;
            this.startStatusPolling();
          }
        }

        if (onAfterUpdate) {
          onAfterUpdate();
        }
      },
      (error: any) => {
        console.error('Failed to check status:', error);
        this.uploadedPapers = [];
      }
    );
  }

  /**
   * Process uploaded papers with AI analysis
   */
  processLiteratureReview(): void {

    if (this.uploadedPapers.length === 0) {
      this.snackBar.open('Nessun documento da elaborare', 'Chiudi', { duration: 3000 });
      return;
    }

    const organizationId = this.userService.getCurrentOrganization()?.id;
    if (!organizationId) {
      this.snackBar.open('Errore: nessuna organizzazione selezionata', 'Chiudi', { duration: 3000 });
      return;
    }

    if (!this.projectId) {
      this.snackBar.open('Errore: nessun progetto selezionato', 'Chiudi', { duration: 3000 });
      return;
    }

    this.isProcessingPapers = true;
    // console.log('tarting AI analysis for uploaded papers:', this.uploadedPapers);
    // console.log('All papers types:', this.uploadedPapers.map(p => ({ type: p.type, id: p.id, PMID: p.PMID, title: p.title })));

    // First, save any new PubMed papers to the database (papers without an ID)
    const newPubmedPapers = this.uploadedPapers.filter(p => p.type === 'PUBMED' && !p.id && p.PMID);
    // console.log('Filtered new PubMed papers (without ID):', newPubmedPapers);

    if (newPubmedPapers.length > 0) {
      // console.log('Saving', newPubmedPapers.length, 'new PubMed papers to database');
      // console.log('Papers to save:', newPubmedPapers);

      // Save each PubMed paper
      let savedCount = 0;
      newPubmedPapers.forEach(paper => {
        this.aiService.savePubmedDocument(
          organizationId,
          this.projectId,
          {
            PMID: paper.PMID,
            ArticleTitle: paper.title,
            link: paper.link
          },
          (response) => {
            console.log('PubMed paper saved successfully:', response);
            savedCount++;
            paper.id = response.id || response.file_id;

            // When all PubMed papers are saved, start the AI analysis
            if (savedCount === newPubmedPapers.length) {
              // console.log('All PubMed papers saved, starting AI analysis');
              this.startLiteratureReviewProcess(organizationId);
            }
          },
          (error) => {
            console.error('Error saving PubMed paper:', error);
            this.isProcessingPapers = false;
            this.snackBar.open('Errore durante il salvataggio degli articoli PubMed', 'Chiudi', { duration: 3000 });
          }
        );
      });
    } else {
      // No new PubMed papers, start processing directly
      this.startLiteratureReviewProcess(organizationId);
    }
  }

  /**
   * Start the literature review AI process and poll for status
   */
  private startLiteratureReviewProcess(organizationId: number): void {
    const payload = {
      organization_id: organizationId,
      project_id: this.projectId
    };

    console.log('Starting AI analysis process:', payload);

    this.aiService.processLiteratureReview(
      payload,
      (result: any) => {
        console.log('AI analysis process started:', result);
        this.snackBar.open('Analisi AI avviata', 'Chiudi', { duration: 3000 });

        // Start polling for status updates
        this.startStatusPolling();
      },
      (error: any) => {
        console.error('AI analysis failed to start:', error);
        this.snackBar.open('Errore durante l\'avvio dell\'analisi AI', 'Chiudi', { duration: 5000 });
        this.isProcessingPapers = false;
      }
    );
  }

  /**
   * Poll for status updates from the backend
   */
  private startStatusPolling(): void {
    // Stop any existing polling first to avoid duplicates
    this.stopStatusPolling();

    console.log('Starting status polling (every 3 seconds)');

    this.statusPollingInterval = setInterval(() => {
      // Check status and evaluate stop condition only after the response arrives
      this.checkAttachmentsStatus(false, () => {
        const hasProcessing = this.uploadedPapers.some(p => p.status === 'processing');

        if (!hasProcessing) {
          this.stopStatusPolling();
          this.isProcessingPapers = false;

          const hasCompleted = this.uploadedPapers.some(p =>
            p.status === 'processed' || p.status === 'completed'
          );

          if (hasCompleted) {
            this.snackBar.open('Analisi AI completata', 'Chiudi', { duration: 3000 });
          }
        }
      });
    }, 5000); // Poll every 5 seconds
  }

  /**
   * Process uploaded papers (legacy method - kept for backwards compatibility)
   */
  processPapers(): void {
    if (this.uploadedPapers.length === 0) return;

    this.isProcessingPapers = true;
    console.log('Processing uploaded papers:', this.uploadedPapers);

    // TODO: Implement actual paper processing logic
    // This should call a backend API to process the PDFs
    // For now, just simulate processing

    setTimeout(() => {
      this.snackBar.open(
        `${this.uploadedPapers.length} documento/i elaborato/i con successo`,
        'Chiudi',
        { duration: 5000 }
      );

      // Clear uploaded papers after processing
      this.uploadedPapers = [];
      this.isProcessingPapers = false;

      // Optionally switch to results tab or refresh literature
      this.loadExistingLiterature();
    }, 2000);
  }

  // ============================================================================
  // NEW AWS LAMBDA-BASED STATEMENT GENERATION METHODS
  // ============================================================================

  /**
   * Generate statements from structured form using new AWS Lambda endpoint
   * @param formData - Already transformed backend payload from the form component
   */
  generateStatementsFromForm(formData: any, selectedSections: string[] = []): void {
    const organizationId = this.userService.getCurrentOrganization()?.id;
    if (!organizationId) {
      this.showMessage('generic.error.no-organization', 'error');
      return;
    }

    // Reset state
    this.newGenerationState = {
      processId: null,
      processName: null,
      status: 'waiting',
      progress: 0,
      message: 'Preparazione richiesta...',
      error: null,
      statements: [],
      sections: [],
      surveyMetadata: null,
      selectedStatementIds: [],
      targetStatements: 4,
      generatedCount: 0,
      appliedToggles: null
    };

    // The formData is already the transformed backend payload from transformToBackendPayload
    // It includes evidence_sources with anchor_studies and internal_sources that have:
    // - anchor_studies with llm_analysis
    // - internal_sources with the survey's questions + user responses + ai_analysis

    const request: NewStatementGenerationRequest = {
      ...formData,
      output_constraints: {
        target_statements: formData.output_constraints?.target_statements ?? 4
      }
    };

    // Optional Section Whisperer output: constrain generation to the reviewed
    // sections. Omitted entirely when the user kept no section, so the added
    // prompt part "leaves" (per spec). n_sections is a whisperer-only knob and
    // must not travel to the generator payload.
    delete (request as any).n_sections;
    if (selectedSections && selectedSections.length > 0) {
      request.selected_sections = selectedSections;
    }

    console.log(' Initiating new statement generation with request:', request);
    console.log(' Evidence sources:', request.evidence_sources);

    // Show progress monitor
    this.showProgressMonitor = true;
    this.showStatementReview = false;
    this.newGenerationState.message = 'Avvio generazione...';

    // Call new endpoint
    this.statementService.initiateStatementGeneration(
      request,
      (response) => {
        console.log('Generation initiated:', response);
        this.newGenerationState.processId = response.process_id;
        this.newGenerationState.processName = response.name;
        this.newGenerationState.status = response.status;
        this.newGenerationState.message = response.message;

        // Start polling
        this.startPollingNewEndpoint(response.process_id);
      },
      (error) => {
        console.error(' Error initiating generation:', error);
        const errorMsg = error.error?.message || 'Errore durante l\'avvio della generazione';
        this.newGenerationState.status = 'failed';
        this.newGenerationState.error = errorMsg;
        this.newGenerationState.error_message = errorMsg;
        // No failed dialog box over the input form — just notify and reset.
        this.showProgressMonitor = false;
        this.formRef?.resetSubmissionState();
        this.showMessage(errorMsg, 'error');
        this.loadPastGenerations();
      }
    );
  }

  /**
   * Start polling for generation status
   */
  private startPollingNewEndpoint(processId: string): void {
    console.log('Starting polling for process:', processId);

    // Il polling usa backoff esponenziale (2s → 15s con jitter ±20%). La pipeline
    // asincrona della Step Function può richiedere molto più tempo della vecchia
    // chiamata sincrona alla Lambda (generazione multi-step + persistenza); tempo massimo 15 min
    this.newPollingSubscription = this.statementService
      .pollGenerationStatus(processId, 2000, 15000, 1.5, 15 * 60 * 1000)
      .subscribe({
      next: (status: GenerationStatusResponse) => {
        console.log('Status update:', status);
        this.newGenerationState.status = status.status;
        this.newGenerationState.progress = status.progress_percentage;
        this.newGenerationState.progress_percentage = status.progress_percentage;
        this.newGenerationState.message = status.message;
        this.newGenerationState.targetStatements = status.target_statements;
        this.newGenerationState.target_statements = status.target_statements;
        this.newGenerationState.generatedCount = status.generated_statements_count;
        this.newGenerationState.generated_statements_count = status.generated_statements_count;
        this.newGenerationState.sections = status.sections;
        this.newGenerationState.appliedToggles = status.applied_toggles;
        this.newGenerationState.applied_toggles = status.applied_toggles;
        this.newGenerationState.surveyMetadata = status.survey_metadata;
        this.newGenerationState.survey_metadata = status.survey_metadata;
        this.newGenerationState.name = status.name;

        if (status.error_message) {
          this.newGenerationState.error = status.error_message;
          this.newGenerationState.error_message = status.error_message;
        }
      },
      complete: () => {
        console.log('Polling completed, status:', this.newGenerationState.status);
        if (this.newGenerationState.status === 'completed') {
          this.loadGeneratedStatementsNew();
        } else if (this.newGenerationState.status === 'failed') {
          // Don't leave a failed dialog box over the input form: hide it, notify
          // briefly, and let the result show in the "Past Generations" tab.
          this.showProgressMonitor = false;
          this.formRef?.resetSubmissionState();
          this.showMessage('statement-generator.generation-failed', 'error');
        }
        this.loadPastGenerations();
      },
      error: (error) => {
        console.error('Polling error:', error);
        // Same: no persistent failed box in the input form.
        this.newGenerationState.status = 'failed';
        this.showProgressMonitor = false;
        this.formRef?.resetSubmissionState();
        this.showMessage('statement-generator.polling-failed', 'error');
        this.loadPastGenerations();
      }
    });
  }

  /**
   * Load generated statements when completed
   */
  private loadGeneratedStatementsNew(): void {
    if (!this.newGenerationState.processId) return;

    console.log('Loading generated statements for process:', this.newGenerationState.processId);

    this.statementService.getGeneratedStatementsNew(
      this.newGenerationState.processId,
      (response) => {
        console.log('Statements loaded:', response);
        this.newGenerationState.statements = response.statements;
        this.newGenerationState.sections = response.sections;
        this.newGenerationState.surveyMetadata = response.survey_metadata;
        this.newGenerationState.selectedStatementIds = [];

        this.showStatementReview = true;
        this.showProgressMonitor = false;
        this.selectedTabIndex = 3; // "Statement Generati" tab (where app-statement-review lives)
        this.formRef?.resetSubmissionState();
      },
      (error) => {
        console.error('Error loading statements:', error);
        this.newGenerationState.status = 'failed';
        // No failed dialog box over the input form — notify and reset.
        this.showProgressMonitor = false;
        this.formRef?.resetSubmissionState();
        this.showMessage('statement-generator.loading-failed', 'error');
        this.loadPastGenerations();
      }
    );
  }

  /**
   * Handle selection change from statement review
   */
  handleSelectionChange(selectedIds: number[]): void {
    this.newGenerationState.selectedStatementIds = selectedIds;
    console.log('Selection changed:', selectedIds);
  }

  /**
   * Handle add to survey
   */
  handleAddToSurvey(statementIds: number[]): void {
    const request: CreateSurveyFromStatementsRequest = {
      statement_ids: statementIds,
      project_id: this.projectId,
    };

    this.statementService.createSurveyFromStatements(
      request,
      (response) => {
        this.lastCreatedSurveyId = response.survey_id;

        // Update local statement state
        this.newGenerationState.statements.forEach(stmt => {
          if (statementIds.includes(stmt.id)) {
            stmt.is_in_draft_survey = true;
            stmt.survey_id = response.survey_id;
          }
        });

        this.toast.success(
          'Survey creata',
          `${response.questions_count} statement aggiunti alla survey "${response.survey_title}"`
        );
      },
      (error) => {
        console.error('Error creating survey from statements:', error);
        this.toast.error('Errore', 'Errore durante la creazione della survey');
      }
    );
  }

  /**
   * Handle export statements
   */
  handleExport(statements: any[]): void {
    console.log('Export statements:', statements);
    // TODO: Implement export functionality (PDF/CSV)
    this.showMessage('Funzionalità di export in arrivo', 'warning');
  }

  /**
   * Handle view statements from progress monitor
   */
  handleViewStatementsFromMonitor(): void {
    this.loadGeneratedStatementsNew();
  }

  /**
   * Handle retry generation
   */
  handleRetryGeneration(): void {
    this.showProgressMonitor = false;
    this.showStatementReview = false;
    this.newGenerationState.status = 'idle';
    this.newGenerationState.error = null;
    this.newGenerationState.error_message = null;
    this.formRef?.resetSubmissionState();
  }

  /**
   * Handle close progress monitor — user dismissed without retrying
   */
  handleCloseProgressMonitor(): void {
    this.showProgressMonitor = false;
    // If generation failed, reset form so user can submit again
    if (this.newGenerationState.status === 'failed') {
      this.newGenerationState.status = 'idle';
      this.newGenerationState.error = null;
      this.newGenerationState.error_message = null;
      this.formRef?.resetSubmissionState();
    }
  }

  // ============================================================================
  // HELPER METHODS FOR BUILDING REQUEST
  // ============================================================================

  /**
   * Build consultation type string from form data
   */
  private buildConsultationType(types: any): string {
    if (!types) return '';
    const selected = [];
    if (types.asynchronousAdvisoryBoard) selected.push('Asynchronous Advisory Board');
    if (types.delphi) selected.push('Delphi');
    if (types.modifiedDelphi) selected.push('Modified Delphi');
    if (types.miniDelphi) selected.push('Mini-Delphi');
    if (types.structuredExpertSurvey) selected.push('Structured Expert Survey');
    if (types.other) selected.push(types.other);
    return selected.join(', ');
  }

  /**
   * Build outcomes string from form data
   */
  private buildOutcomes(outcomes: any): string {
    if (!outcomes) return '';
    const selected = [];
    if (outcomes.pfs) selected.push('PFS');
    if (outcomes.os) selected.push('OS');
    if (outcomes.orr) selected.push('ORR');
    if (outcomes.cr) selected.push('≥CR');
    if (outcomes.safetyInfections) selected.push('Safety/Infections');
    if (outcomes.qolPro) selected.push('QoL/PRO');
    if (outcomes.mrdThreshold) selected.push(`MRD (${outcomes.mrdThreshold})`);
    if (outcomes.other) selected.push(outcomes.other);
    return selected.join(', ');
  }

  /**
   * Build setting string from form data
   */
  private buildSetting(setting: any): string {
    if (!setting) return '';
    const selected = [];
    if (setting.trial) selected.push('Trial');
    if (setting.realWorld) selected.push('Real-world');
    if (setting.mixed) selected.push('Mixed');
    if (setting.review) selected.push('Review');
    if (setting.guidelines) selected.push('Guidelines');
    return selected.join(', ');
  }

  /**
   * Build unmet needs ranked list from form data
   */
  private buildUnmetNeeds(needs: any): string[] {
    if (!needs) return [];
    const ranked = [];
    if (needs.efficacyDepth) ranked.push('Efficacy depth (CR / MRD)');
    if (needs.durability) ranked.push('Durability (PFS / OS / DOR)');
    if (needs.safetyInfections) ranked.push('Safety / Infections');
    if (needs.accessTime) ranked.push('Access / Time-to-treatment');
    if (needs.sequencing) ranked.push('Sequencing post-target');
    if (needs.qolPro) ranked.push('QoL / PRO');
    if (needs.horizonScan) ranked.push('Horizon scan / future options');
    return ranked.map((need, index) => `${index + 1}. ${need}`);
  }

  /**
   * Extract reference ID from study reference
   */
  private extractRefId(reference: string): string {
    if (!reference) return '';

    // Extract PMID if present
    const pmidMatch = reference.match(/PMID:\s*(\d+)/i);
    if (pmidMatch) return `pmid:${pmidMatch[1]}`;

    // Extract DOI if present
    const doiMatch = reference.match(/DOI:\s*([\d.\/]+)/i);
    if (doiMatch) return `doi:${doiMatch[1]}`;

    // Extract NCT if present
    const nctMatch = reference.match(/NCT:\s*(NCT\d+)/i);
    if (nctMatch) return `nct:${nctMatch[1]}`;

    return '';
  }

  /**
   * Generate a summary for a survey
   */
  private generateSurveySummary(survey: any): string {
    if (!survey) return '';

    let summary = `Survey with ${survey.questions?.length || 0} questions`;
    if (survey.responses?.length > 0) {
      summary += ` and ${survey.responses.length} responses`;
    }
    if (survey.description) {
      summary += `. ${survey.description.substring(0, 100)}`;
    }
    return summary;
  }
}

// Dialog Component for displaying AI Analysis
@Component({
  selector: 'app-analysis-dialog',
  template: `
    <div class="analysis-dialog-wrapper">
      <!-- Header -->
      <div class="analysis-dialog-header" [class.error-header]="data.isError">
        <div class="header-content">
          <mat-icon class="header-icon">{{ data.isError ? 'error_outline' : 'psychology' }}</mat-icon>
          <h2 class="header-title">{{ data.isError ? 'Error Details' : 'AI Analysis' }}</h2>
        </div>
        <button mat-icon-button class="close-button" (click)="close()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <!-- Content -->
      <mat-dialog-content class="analysis-dialog-content">
        <div class="paper-info">
          <mat-icon class="paper-icon">description</mat-icon>
          <div class="paper-details">
            <span class="paper-label">Document:</span>
            <span class="paper-title">{{ data.title }}</span>
          </div>
        </div>

        <div class="analysis-section" [class.error-section]="data.isError">
          @if (data.isError) {
            <pre class="error-text">{{ data.content }}</pre>
          }
          @else {
            <markdown [data]="data.content" class="markdown-content"></markdown>
          }
        </div>
      </mat-dialog-content>

      <!-- Footer -->
      <mat-dialog-actions class="analysis-dialog-actions">
        <button mat-raised-button color="primary" (click)="close()">
          <mat-icon>check</mat-icon>
          Close
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styleUrls: ['./statement-generator.component.scss'],
  standalone: false
})
export class AnalysisDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<AnalysisDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { title: string; content: string; isError: boolean }
  ) {}

  close(): void {
    this.dialogRef.close();
  }
}
